"use client";

/**
 * MuxVideoPlayerWithCTA - Estilo TikTok Shop (production-ready)
 *
 * Single, scalable component for multiple videos + CTA. The CTA (redirect URL,
 * label, icon) is per-page: each static page has its own setup via
 * staticPageVideoConfig + env keys (e.g. VITE_MEJORES_AMIGOS_*, VITE_TELEGRAM_GROUPS_*).
 * Funnel "one asset first": `VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID` (Mux ID or stream.mux.com URL)
 * feeds Telegram/channel_invite when per-locale IDs are unset; optional `VITE_MUX_CTA_FREE_JOIN_COPY`
 * switches CTA copy to "join free" (see videoCTAConfig). Cartel sutil encima del CTA:
 * `VITE_MUX_CTA_FRIENDLY_BANNER=true` o free-join; apagar: `VITE_MUX_CTA_FRIENDLY_BANNER=false` (y sin free-join).
 *
 * Add new pages by extending STATIC_PAGE_VIDEO_REGISTRY; no component changes.
 *
 * Usage:
 * - Static pages: `page` or pathname → config per page (video + CTA redirect).
 * - BioLinkTemplate modal replacement: use `variant="modal"` + config (playbackId, ctaUrl,
 *   ctaLabel, onClose, onCTAClick). One modal block, config-driven; any button can open it
 *   with its own config. You define which modal to replace when wiring the flow.
 * - Overlays / other: manual playbackId/ctaUrl or `service`.
 *
 * Config priority: manual > page > service. Con Cloudflare, `resolveWorkerDrivenCta()` puede
 * sustituir URL/label del CTA salvo `skipWorkerTemplateCta`.
 *
 * País para pricing / URLs por país: prop `countryCode` si viene del padre (Worker); si no,
 * `detectCountryCode()` (prioriza `getServerTemplateConfig()` → __TEMPLATE_CONFIG__ o cookies).
 */

import {
  type StaticPageVideoPage,
  getStaticPageFromPathname,
  getStaticPageVideoConfig,
} from "@/config/staticPageVideoConfig";
import {
  CREATE_PAYMENT_INTENT_ENDPOINT,
  PACKS,
  STRIPE_APPEARANCE,
  STRIPE_PUBLISHABLE_KEY,
  getPackPricing as getPackPricingStatic,
} from "@/config/stripe";
import {
  getTelegramCTAUrlByCountry,
  getTelegramPlaybackIdByCountry,
} from "@/config/telegramGroupsVideo";
import {
  type MuxFriendlyFunnelOpts,
  type SupportedLanguage,
  type VideoCTAService,
  getLocalizedLabel,
  getLocalizedTitle,
  getMuxCtaFriendlyBannerMessage,
  getVideoCTAConfigWithPayment,
} from "@/config/videoCTAConfig";
import { trackButtonClickGA } from "@/lib/analytics";
import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
import { getMercadoPagoCheckoutBasicArUrl } from "@/lib/mercadopagoArCheckoutUrl";
import type { PackConfig } from "@/lib/api";
import { detectCountryCode } from "@/lib/countryDetection";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import {
  getUserLanguage,
  usePackConfigForVideo,
} from "@/lib/packConfigHelpers";
import { fetchCheckoutJson } from "@/lib/fetchCheckoutApi";
import { trackButtonClickPostHog, trackStripePayFailurePostHog } from "@/lib/posthogAnalytics";
import { getDataFastStripeMetadata, trackDataFastGoal } from "@/lib/datafast";
import { resolveMuxErrorFallbackPlaybackId } from "@/lib/muxBrandPlayback";
import { membershipVipCta } from "@/lib/membershipCtaCopy";
import {
  inferSlackFunnelTemplateFromPath,
  trackMuxVideoFailureSlack,
  type MuxVideoFailureContext,
} from "@/lib/trackMuxVideoFailureSlack";
import {
  computeStripePackCheckoutAmounts,
  getOrCreateSessionWatcherLuckPercent,
  getWatcherLuckDiscountMaxPercentClient,
} from "@/lib/watcherLuckDiscount";
import { usePackConfig } from "@/lib/usePackConfig";
import {
  getWorkerCtaDisplayLabel,
  resolveWorkerDrivenCta,
} from "@/lib/workerDrivenCta";
import MuxPlayer from "@mux/mux-player-react";
import type { HlsConfig } from "hls.js";
import { Elements } from "@stripe/react-stripe-js";
import { type Stripe, loadStripe } from "@stripe/stripe-js";
import { useAuth, useUser } from "@clerk/clerk-react";
import { ExternalLink, Instagram, Loader2, Play, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { StripeCheckoutForm } from "./StripeCheckoutForm";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { isMuxVideoDisplayEnabled } from "@/lib/muxDisplayEnabled";
import { launchIntlExclusiveMembershipCheckout } from "@/lib/intlExclusiveMembershipCheckout";
import { CheckoutEmailCaptureModal } from "@/components/CheckoutEmailCaptureModal";
import { resolvePreCheckoutEmail } from "@/lib/preCheckoutEmail";
import {
  MUX_HIDE_NATIVE_CONTROLS_VARS,
  buildMuxLqipUrl,
  buildMuxThumbnailUrl,
  muxHideNativeControlsCss,
  muxPosterWidthBucket,
} from "@/lib/muxPlayerPresentation";
import {
  buildMuxDataMetadata,
  getMuxDataEnvKey,
} from "@/lib/muxDataTracking";
import MuxPosterBackdrop from "./MuxPosterBackdrop";

const SAFE_PLAYBACK_ID_FALLBACK = "mux-cta-default";

// ── Mux Data (web) — ver src/lib/muxDataTracking.ts ──────────────────────────
// Env key + metadata unificados para admin /admin/mux-video-analytics.
// NO usar @mux/mux-data-react-native-video (solo RN).

/** "Suerte" toast: no mostrar al iniciar play; solo tras contexto de vista real o fallback de tiempo. */
const WATCHER_LUCK_MIN_PLAYHEAD_SEC = 8.25;
const WATCHER_LUCK_FALLBACK_WALL_MS = 17_000;
const WATCHER_LUCK_TOAST_MS = 5_400;

/**
 * Log para producción: trackea todos los eventos de video y errores de red
 * Envía logs al servidor (Axiom) y PostHog para análisis
 */
function logVideoEvent(payload: {
  event:
    | "video_error"
    | "video_load_start"
    | "video_load_success"
    | "video_load_failed"
    | "video_play_attempt"
    | "video_play_success"
    | "video_play_failed"
    | "video_stalled"
    | "video_buffering"
    | "network_error"
    | "playback_id_invalid"
    | "fallback_attempted"
    | "fallback_failed"
    | "video_shown_thumbnail_only";
  country?: string;
  service?: string;
  playbackId?: string;
  errorType?: string;
  errorMessage?: string;
  errorCode?: string | number;
  networkState?: number;
  readyState?: number;
  isNetworkError?: boolean;
  isTokenError?: boolean;
  fallbackAttempted?: boolean;
  userAgent?: string;
  connectionType?: string;
  duration?: number;
  currentTime?: number;
}) {
  const data = {
    source: "MuxVideoPlayerWithCTA",
    funnel_goal: "membership_conversion",
    ...(() => {
      const dv = getServerTemplateConfig()?.dualVariant;
      if (!dv) return {};
      return {
        ...(dv.id ? { dual_variant_id: dv.id } : {}),
        ...(dv.priceUsd != null && dv.priceUsd > 0
          ? { dual_variant_price_usd: dv.priceUsd }
          : {}),
        ...(dv.muxPlaybackId
          ? { mux_playback_id_config: dv.muxPlaybackId.slice(0, 48) }
          : {}),
      };
    })(),
    ...payload,
    ts: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    // Network info si está disponible
    connectionType:
      (navigator as { connection?: { effectiveType?: string } }).connection
        ?.effectiveType || "unknown",
    connectionDownlink:
      (navigator as { connection?: { downlink?: number } }).connection
        ?.downlink || null,
  };

  // Log en consola para debugging
  if (payload.event.includes("error") || payload.event.includes("failed")) {
    console.error("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
  } else {
    console.log("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
  }

  // PostHog para analytics
  if (
    typeof window !== "undefined" &&
    (window as { posthog?: { capture: (e: string, p?: object) => void } })
      .posthog?.capture
  ) {
    (
      window as { posthog: { capture: (e: string, p?: object) => void } }
    ).posthog.capture("mux_video_event", data);
  }

  // Axiom: solo mismo origen o proxy. URLs `*.axiom.co` sin CORS en navegador → preflight falla.
  if (typeof window !== "undefined") {
    const axiomEndpoint =
      (import.meta.env.VITE_AXIOM_INGEST_ENDPOINT as string) ||
      (import.meta.env.NEXT_PUBLIC_AXIOM_INGEST_ENDPOINT as string);
    const axiomToken =
      (import.meta.env.VITE_AXIOM_TOKEN as string) ||
      (import.meta.env.NEXT_PUBLIC_AXIOM_TOKEN as string);
    const raw = axiomEndpoint?.trim() ?? "";
    if (raw) {
      let allowBrowser = false;
      try {
        if (raw.startsWith("/")) {
          allowBrowser = true;
        } else {
          const host = new URL(raw).hostname.toLowerCase();
          allowBrowser = !host.endsWith("axiom.co");
        }
      } catch {
        allowBrowser = false;
      }

      if (allowBrowser) {
        fetch(raw, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-ndjson",
            ...(axiomToken && axiomToken.trim() !== ""
              ? { Authorization: `Bearer ${axiomToken}` }
              : {}),
          },
          body: JSON.stringify(data) + "\n",
          keepalive: true,
        }).catch(() => {
          /* fire-and-forget */
        });
      }
    }
  }
}

// Mantener compatibilidad con código existente
function logVideoErrorProduction(payload: Parameters<typeof logVideoEvent>[0]) {
  logVideoEvent(payload);
}

// Checkout flow states
type CheckoutState =
  | "VIDEO_PREVIEW"
  | "CHECKOUT"
  | "PROCESSING"
  | "SUCCESS"
  | "ERROR";

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

/** Sanitiza playbackId para uso en CSS/className (evita XSS). */
function sanitizePlaybackIdForCss(id: string): string {
  const safe = id.replace(/[^a-zA-Z0-9_-]/g, "");
  return safe.length > 0 ? safe : SAFE_PLAYBACK_ID_FALLBACK;
}

/** Valida que la URL sea http/https o relativa (rechaza javascript:, data:, etc.). */
function isAllowedRedirectUrl(url: string): boolean {
  // Allow relative URLs (starting with /)
  if (url.startsWith("/")) {
    return true;
  }

  // For absolute URLs, check protocol
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

/**
 * Detect iOS devices (iPhone, iPad, iPod)
 */
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPad with iOS 13+
    setIsIOS(isIOSDevice);
  }, []);

  return isIOS;
};

/**
 * Detect Android devices
 */
const useIsAndroid = () => {
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    setIsAndroid(/android/.test(userAgent));
  }, []);

  return isAndroid;
};

/**
 * Chromium desktop en macOS (Chrome, Brave, Arc, Opera…) y Arc explícito.
 * Arc a veces no incluye "Arc/" en el user agent; el síntoma típico es fallo MSE en el buffer
 * de audio (`SourceBuffer`/`appendBuffer`). Activamos el mismo endurecimiento para todo
 * Chrome en Mac (no Safari, no Edge).
 */
function detectMuxDesktopMacMseHardening(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/\bArc\/\d/i.test(ua)) return true;
  const brands = (
    navigator as Navigator & {
      userAgentData?: { brands?: { brand: string }[] };
    }
  ).userAgentData?.brands;
  if (brands?.some((b) => b.brand === "Arc")) return true;

  const mac = /Mac OS X|Macintosh/.test(ua);
  const chromiumNotEdge = /\bChrome\/\d/.test(ua) && !/\bEdg\/\d/.test(ua);

  return mac && chromiumNotEdge;
}

const useMuxDesktopMacMseHardening = () =>
  useMemo(() => detectMuxDesktopMacMseHardening(), []);

/**
 * Detect if device is in landscape orientation
 */
const useIsLandscape = () => {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkOrientation = () => {
      // Use screen.orientation if available, fallback to window dimensions
      if (screen.orientation) {
        setIsLandscape(screen.orientation.type.includes("landscape"));
      } else {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  return isLandscape;
};

/**
 * Config for using MuxVideoPlayerWithCTA as overlay in BioLinkTemplate (fullscreen reel or modal).
 * Pass variant + these props + onClose. "fullscreen" = reel-style full viewport; "modal" = centered panel.
 */
export type VideoCTAModalConfig = Pick<
  MuxVideoPlayerWithCTAProps,
  | "service"
  | "playbackId"
  | "ctaUrl"
  | "ctaLabel"
  | "title"
  | "openInNewTab"
  | "displayMode"
  | "loop"
  | "playbackIdByCountry"
  | "ctaUrlByCountry"
  | "countryCode"
  | "language"
  | "skipWorkerTemplateCta"
> & {
  onCTAClick?: (url: string) => void;
  /** "fullscreen" = reel full viewport (default); "modal" = centered panel. */
  variant?: "fullscreen" | "modal";
};

/**
 * Telemetría ligera de posición de reproducción (BioTemplatePremium / funnel).
 * `timeupdate` está limitada a ~1 evento / 4s para no saturar PostHog.
 */
export type PlaybackTelemetryPayload = {
  currentTimeSeconds: number;
  durationSeconds: number | null;
  playbackId: string;
  reason: "timeupdate" | "pause" | "ended";
};

export interface MuxVideoPlayerWithCTAProps {
  // Option 1: Use service type for automatic configuration
  service?: VideoCTAService;
  countryCode?: string;
  language?: "en" | "es";

  // Option 2: Static page – video + CTA per page. CTA redirect/label/icon come from
  // staticPageVideoConfig (registry + env). Set per page according to needs.
  page?: StaticPageVideoPage;

  // Option 3: Manual configuration (overrides service/page config if provided)
  playbackId?: string;
  /**
   * Si true, el playbackId manual tiene prioridad absoluta y NO se usan fallbacks por país.
   * Útil para templates con videos específicos (e.g. BioTemplatePremium).
   */
  forceManualPlaybackId?: boolean;
  title?: string;
  ctaUrl?: string; // Redirect al hacer click en "Unirte"
  ctaLabel?: string; // default: "Unirte"
  /** Texto opcional arriba del botón CTA (ej. "Ingresar al Canal Privado + Hablar conmigo") */
  ctaSubtitle?: string;
  /**
   * Overlay VIP en BioTemplatePremiumSimple: cartel "no es VIP" cuando el CTA abre Telegram gratis
   * (`premiumSimpleMembershipFreeFunnel` + `getMuxCtaFriendlyBannerMessage`).
   */
  premiumSimpleMembershipFreeFunnel?: boolean;
  /** Resolución admin + env para el cartel "no es VIP" (Invitación premium en DB). */
  muxFriendlyFunnel?: MuxFriendlyFunnelOpts | null;
  openInNewTab?: boolean;

  // Option 4: Country-specific video configurations
  // Supports per-country videos for different languages/regions
  playbackIdByCountry?: {
    // North America
    US?: string;

    // Europe
    ES?: string; // Spain
    DE?: string; // Germany
    FR?: string; // France

    // LATAM (BioLinkTemplate countries)
    MX?: string; // Mexico
    CL?: string; // Chile
    CO?: string; // Colombia
    PE?: string; // Peru
    PY?: string; // Paraguay
    AR?: string; // Argentina
    BR?: string; // Brazil

    // Middle East
    AE?: string; // UAE (United Arab Emirates / Emiratos)

    // Fallback for other countries
    default?: string;
  };

  // Option 5: Country-specific CTA URL configurations (Stripe Payment Links)
  // Different pricing per country - managed via Railway env vars
  ctaUrlByCountry?: {
    // North America
    US?: string; // e.g. $14.99 USD

    // Europe
    ES?: string; // e.g. €12.99 EUR (Spain)
    DE?: string; // e.g. €14.99 EUR (Germany)
    FR?: string; // e.g. €13.99 EUR (France)

    // LATAM (BioLinkTemplate countries) - Each country can have different pricing
    MX?: string; // e.g. $199 MXN (Mexico)
    CL?: string; // e.g. CLP pricing (Chile)
    CO?: string; // e.g. COP pricing (Colombia)
    PE?: string; // e.g. PEN pricing (Peru)
    PY?: string; // e.g. PYG pricing (Paraguay)
    AR?: string; // e.g. ARS pricing (Argentina)
    BR?: string; // e.g. R$49 BRL (Brazil)

    // Middle East
    AE?: string; // e.g. AED 49 (UAE / Emiratos)

    // Fallback for other countries (generic LATAM pricing)
    default?: string;
  };

  displayMode?: "reel" | "contain" | "cover" | "auto";
  loop?: boolean;
  
  /** 
   * Control de autoplay. Por defecto "any" (intenta con sonido, fallback muted).
   * - "any": autoplay con sonido, fallback muted (TikTok/Reels style)
   * - "muted": autoplay solo muted
   * - false: no autoplay, requiere click del usuario
   */
  autoPlay?: "any" | "muted" | false;

  /** 'fullscreen' = fill container (default). 'modal' = backdrop + centered rounded panel, Escape/backdrop to close. */
  variant?: "fullscreen" | "modal";

  /** When set, custom checkout uses this pack (e.g. exclusive, sorpresa) for pricing and payment instead of channel_invite. */
  overridePackId?: string;

  /** Si true, no se muestra el aviso "Toca para escuchar". Solo FullscreenExclusive (/exclusive) debe pasarlo; otras rutas no. */
  hideUnmutePrompt?: boolean;

  /**
   * Si true, no aplica el CTA inyectado por Cloudflare (`window.__TEMPLATE_CONFIG__`).
   * Por defecto false: `hideCta` vacía el CTA; `showCommunity` / `showBirthday` / `showWishlist` sustituyen URL y texto.
   */
  skipWorkerTemplateCta?: boolean;

  /**
   * Posición del indicator "Tap for sound" cuando el browser bloquea autoplay
   * con sonido. Default: "top-right" (chip pequeño esquinado, sutil).
   * "center": pill centrado en el medio del video — más visible para indicar
   * que es accionable, pero igual sutil (opacity 0.85, backdrop blur). Útil
   * en surfaces tipo TikTok shop fullscreen donde queremos guiar al user a
   * activar sonido sin esconder el indicator en una esquina.
   */
  unmuteIndicatorPosition?: "top-right" | "center";

  onReady?: () => void;
  onPlay?: () => void;
  onEnded?: () => void;
  onCTAClick?: (url: string) => void;
  onClose?: () => void;
  /** Fired throttled (~4s) on timeupdate, and always on pause/ended — para métricas de cuánto vio el video. */
  onPlaybackTelemetry?: (payload: PlaybackTelemetryPayload) => void;
  /** Fired when Mux reports a confirmed video error (after retries, fatal to the user). */
  onError?: (message: string) => void;
  /** Autoplay bloqueado o stream no arrancó — el visitor debe tocar play (membership overlay). */
  onNeedsUserGesture?: () => void;

  /** Template name for Slack funnel alerts (defaults to pathname inference). */
  slackFunnelTemplate?: string;
  /** Funnel flags at error time (CTA clicks, variant, etc.) — merged into Slack payload. */
  slackFunnelContext?: Partial<
    Pick<
      MuxVideoFailureContext,
      | "videoStarted"
      | "videoCompleted"
      | "ctaVipClicked"
      | "ctaFreeClicked"
      | "checkoutOpened"
      | "timeOnPageSeconds"
      | "dualVariantId"
      | "dualVariantGeoScope"
      | "dualVariantGeoCode"
    >
  >;

  className?: string;
  containerClassName?: string;

  /**
   * "page" = viewport del browser (100dvh). "container" = llena el padre
   * (iframe admin, hero con h-full). Evita min-h 400px / 90vh que recortan mobile.
   */
  viewportScope?: "page" | "container";
}

// Translation helper for UI texts
type TranslationKey =
  | "preparingCheckout"
  | "openingCheckoutPage"
  | "paymentError"
  | "paymentErrorMsg"
  | "retry"
  | "back"
  | "enableAudio"
  | "tapForSound"
  | "tapToPlayWithSound"
  | "openInBrowser"
  | "betterExperience"
  | "linkCopied"
  | "pasteInBrowser"
  | "longPressHint"
  | "loading"
  | "loadingVideo"
  | "connectingToPlayer"
  | "inAppBrowserVideoLoadHint"
  | "inAppBrowserMenuHint"
  | "startingPlayback"
  | "slowConnection"
  | "optimizingStream"
  | "decodingPreview"
  | "playbackReadyBrief";

const translations: Record<TranslationKey, Record<"en" | "es", string>> = {
  preparingCheckout: {
    en: "Preparing checkout...",
    es: "Preparando checkout...",
  },
  openingCheckoutPage: {
    en: "Opening checkout — please wait…",
    es: "Abriendo el checkout — esperá un momento…",
  },
  paymentError: {
    en: "Payment error",
    es: "Error en el pago",
  },
  paymentErrorMsg: {
    en: "Something went wrong. Please try again.",
    es: "Hubo un problema al procesar tu pago. Por favor, intenta de nuevo.",
  },
  retry: {
    en: "Try again",
    es: "Reintentar",
  },
  back: {
    en: "Back",
    es: "Volver",
  },
  enableAudio: {
    en: "Enable audio",
    es: "Activar audio",
  },
  tapForSound: {
    en: "🔊 Tap for sound",
    es: "🔊 Toca para escuchar",
  },
  tapToPlayWithSound: {
    en: "🔊 Tap to play with sound",
    es: "🔊 Toca para reproducir con audio",
  },
  openInBrowser: {
    en: "Open in Browser",
    es: "Abrir en Navegador",
  },
  betterExperience: {
    en: "For the best experience, open this page in your browser",
    es: "Para una mejor experiencia, abre esta página en tu navegador",
  },
  linkCopied: {
    en: "Link copied!",
    es: "¡Enlace copiado!",
  },
  pasteInBrowser: {
    en: "Paste in Safari or Chrome to open",
    es: "Pega en Safari o Chrome para abrir",
  },
  longPressHint: {
    en: "Long-press here to open in Safari",
    es: "Mantén pulsado aquí para abrir en Safari",
  },
  loading: {
    en: "Loading...",
    es: "Cargando...",
  },
  loadingVideo: {
    en: "Loading video...",
    es: "Cargando video...",
  },
  connectingToPlayer: {
    en: "Connecting to the player…",
    es: "Conectando con el reproductor…",
  },
  inAppBrowserVideoLoadHint: {
    en: "Instagram / Facebook browser can be a bit slower — thanks for your patience.",
    es: "En el navegador de Instagram o Facebook la carga puede tardar un poco más.",
  },
  inAppBrowserMenuHint: {
    en: "Tip: tap ⋮ or \"…\" (top) → Open in browser for smoother playback.",
    es: "Tip: tocá ⋮ o \"…\" (arriba) → Abrir en el navegador para mejor reproducción.",
  },
  startingPlayback: {
    en: "Starting playback…",
    es: "Iniciando reproducción…",
  },
  slowConnection: {
    en: "Slow connection detected. Still loading your video...",
    es: "Detectamos red lenta. Seguimos cargando el video...",
  },
  optimizingStream: {
    en: "Optimizing stream quality...",
    es: "Optimizando calidad para tu conexión...",
  },
  decodingPreview: {
    en: "Caching the opening frames…",
    es: "Preparando los primeros fotogramas…",
  },
  playbackReadyBrief: {
    en: "Video ready — starting playback.",
    es: "Video listo — iniciando reproducción.",
  },
};

function t(key: TranslationKey, lang: "en" | "es"): string {
  return translations[key][lang];
}

const MuxVideoPlayerWithCTA: React.FC<MuxVideoPlayerWithCTAProps> = ({
  // Service-based configuration
  service,
  countryCode, // When passed (e.g. BioLinkTemplate overlay), used for pricing; else fallback to detected
  language, // Will be auto-detected if not provided

  // Static page – different video + CTA per route
  page: pageProp,

  // Manual configuration (overrides service/page if provided)
  playbackId: manualPlaybackId,
  forceManualPlaybackId = false,
  title: manualTitle,
  ctaUrl: manualCtaUrl,
  ctaLabel: manualCtaLabel,
  ctaSubtitle,
  premiumSimpleMembershipFreeFunnel = false,
  muxFriendlyFunnel = null,
  openInNewTab = false,
  hideUnmutePrompt = false,
  skipWorkerTemplateCta = false,
  unmuteIndicatorPosition = "top-right",

  // Country-specific video configurations
  playbackIdByCountry,
  ctaUrlByCountry,

  displayMode = "auto",
  loop = true,
  autoPlay = "any",
  variant = "fullscreen",
  overridePackId,
  onReady,
  onPlay,
  onEnded,
  onCTAClick,
  onClose,
  onPlaybackTelemetry,
  onError: onErrorProp,
  onNeedsUserGesture,
  slackFunnelTemplate,
  slackFunnelContext,
  className = "",
  containerClassName = "",
  viewportScope = "page",
}) => {
  const effectivePackId = overridePackId ?? "channel_invite";
  const { pathname } = useLocation();
  const { getPackPricing, packs } = usePackConfig();
  // Clerk gate antes del Stripe checkout: cuando el visitor no está signed-in /
  // sin perfil completo, lo mandamos a /registro (Clerk sign-up) con el lang
  // detectado (ES/EN) + return_to para volver a esta misma página y reabrir
  // el CTA después de completar el alta. La gate solo aplica al path "Stripe
  // membership" (service=channel_invite o overridePackId); URLs externas no se
  // gatean para no romper landings tipo Telegram pre-pago.
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const [preCheckoutEmailModalOpen, setPreCheckoutEmailModalOpen] =
    useState(false);
  const pendingIntlVipRef = useRef<{
    effectiveCountry: string;
    languageForPlans: string;
  } | null>(null);

  // Detect user's country on mount - initialize immediately for correct pricing/video
  // This runs synchronously on first render to avoid flash of wrong content
  const [detectedCountry, setDetectedCountry] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      const country = detectCountryCode();
      if (country && import.meta.env.DEV) {
        console.log(
          "[MuxVideoPlayerWithCTA] Initial country detection:",
          country,
        );
      }
      return country;
    }
    return null;
  });

  // Re-leer país tras el primer paint: mismo pipeline estándar (Cloudflare + cookies normalizadas).
  useEffect(() => {
    const sync = () => {
      const next = detectCountryCode();
      if (next) {
        setDetectedCountry((prev) => (prev === next ? prev : next));
      }
    };
    sync();
    const raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Network fallback: when playback fails, try English playback ID (para que siempre haya video)
  const [networkErrorFallbackPlaybackId, setNetworkErrorFallbackPlaybackId] =
    useState<string | null>(null);
  const hasTriedFallbackRef = useRef(false);

  // Stable ref for onError prop — avoids adding it as dep to handleError useCallback
  const onErrorRef = useRef(onErrorProp);
  useEffect(() => { onErrorRef.current = onErrorProp; }, [onErrorProp]);
  const slackFunnelContextRef = useRef(slackFunnelContext);
  useEffect(() => {
    slackFunnelContextRef.current = slackFunnelContext;
  }, [slackFunnelContext]);
  const onNeedsUserGestureRef = useRef(onNeedsUserGesture);
  useEffect(() => {
    onNeedsUserGestureRef.current = onNeedsUserGesture;
  }, [onNeedsUserGesture]);

  const signalNeedsUserGesture = useCallback(() => {
    try {
      onNeedsUserGestureRef.current?.();
    } catch {
      // best-effort
    }
  }, []);

  // Language: Spanish for Spanish-speaking countries, English for all others.
  // Country is the single source of truth — browser language is ignored.
  const SPANISH_COUNTRIES = new Set([
    "AR","MX","CO","CL","PE","PY","ES","BO","EC","GT",
    "HN","NI","PA","DO","CU","CR","VE","UY","PR",
  ]);
  const detectedLanguage: "en" | "es" =
    language ??
    (SPANISH_COUNTRIES.has((countryCode ?? detectedCountry ?? "").toUpperCase()) ? "es" : "en");

  // Static-page config (sync): when `page` prop or pathname matches a known static page
  const resolvedPage: StaticPageVideoPage | null =
    pageProp ??
    (!manualPlaybackId && !manualCtaUrl && !service
      ? getStaticPageFromPathname(pathname)
      : null);
  const pageConfig = resolvedPage
    ? getStaticPageVideoConfig(resolvedPage)
    : null;

  // Load configuration from service if provided (and not using page config)
  const [serviceConfig, setServiceConfig] = useState<{
    playbackId: string;
    ctaUrl: string;
    ctaLabel: string;
    title?: string;
    playbackIdByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
    ctaUrlByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
  } | null>(null);

  // Initialize isLoadingConfig based on whether we need to load service config
  // This prevents race condition where playbackId is "" during initial render
  const needsServiceConfig = Boolean(
    service && !manualPlaybackId && !manualCtaUrl && !resolvedPage,
  );
  const [isLoadingConfig, setIsLoadingConfig] = useState(needsServiceConfig);

  useEffect(() => {
    if (needsServiceConfig) {
      setIsLoadingConfig(true);
      getVideoCTAConfigWithPayment(service!, countryCode, detectedLanguage)
        .then((config) => {
          if (config.hasVideo) {
            setServiceConfig({
              playbackId: config.playbackId,
              ctaUrl: config.ctaUrl,
              ctaLabel: getLocalizedLabel(config, language),
              title: getLocalizedTitle(config, language),
              playbackIdByCountry: config.playbackIdByCountry,
              ctaUrlByCountry: config.ctaUrlByCountry,
            });
          } else {
            setServiceConfig(null);
          }
          setIsLoadingConfig(false);
        })
        .catch((error) => {
          console.error(
            "[MuxVideoPlayerWithCTA] Error loading service config:",
            error,
          );
          setIsLoadingConfig(false);
        });
    } else {
      setServiceConfig(null);
      setIsLoadingConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsServiceConfig,
    service,
    countryCode,
    language, // Use original prop, not derived detectedLanguage
  ]);

  // Select playback ID based on country if playbackIdByCountry is provided
  const getCountrySpecificPlaybackId = (): string => {
    // Priority: manual playbackIdByCountry > service config playbackIdByCountry
    const countryConfig =
      playbackIdByCountry || serviceConfig?.playbackIdByCountry;

    if (!countryConfig) return "";

    const country = countryCode ?? detectedCountry ?? "US";
    const upperCountry = country.toUpperCase();

    // Type-safe country check
    type CountryCode = keyof typeof countryConfig;

    // Check for specific country match - supports all configured countries
    if (
      upperCountry in countryConfig &&
      countryConfig[upperCountry as CountryCode]
    ) {
      console.log(
        `[MuxVideoPlayerWithCTA] Using ${country}-specific video${
          service ? ` for ${service}` : ""
        }`,
      );
      return countryConfig[upperCountry as CountryCode] as string;
    }

    // Fallback to default if provided
    if (countryConfig.default) {
      console.log(
        `[MuxVideoPlayerWithCTA] Using default video for country: ${country}${
          service ? ` (${service})` : ""
        }`,
      );
      return countryConfig.default;
    }

    return "";
  };

  // Select CTA URL based on country if ctaUrlByCountry is provided
  const getCountrySpecificCtaUrl = (): string => {
    // Priority: manual ctaUrlByCountry > service config ctaUrlByCountry
    const countryConfig = ctaUrlByCountry || serviceConfig?.ctaUrlByCountry;

    if (!countryConfig) {
      if (!skipWorkerTemplateCta && import.meta.env.DEV) {
        console.log(
          `[MuxVideoPlayerWithCTA] No ctaUrlByCountry config available`,
        );
      }
      return "";
    }

    const country = countryCode ?? detectedCountry ?? "US";
    const upperCountry = country.toUpperCase();

    // Debug: Log available country URLs
    console.log(`[MuxVideoPlayerWithCTA] 🌍 Country Detection:`, {
      detectedCountry,
      countryCodeProp: countryCode,
      resolvedCountry: upperCountry,
      service,
      availableCountries: Object.keys(countryConfig).filter(
        (k) => countryConfig[k as keyof typeof countryConfig],
      ),
    });

    // Type-safe country check
    type CountryCode = keyof typeof countryConfig;

    // Check for specific country match - supports all configured countries
    // This allows per-country pricing: US $14.99, ES €12.99, MX $199 MXN, etc.
    const countryUrl =
      upperCountry && upperCountry in countryConfig
        ? countryConfig[upperCountry as CountryCode]
        : null;

    if (countryUrl && countryUrl.length > 0) {
      console.log(
        `[MuxVideoPlayerWithCTA] ✅ Using ${upperCountry}-specific CTA URL${
          service ? ` for ${service}` : ""
        }:`,
        countryUrl.substring(0, 50) + "...",
      );
      return countryUrl as string;
    }

    // Fallback to default if provided
    if (countryConfig.default) {
      console.log(
        `[MuxVideoPlayerWithCTA] ⚠️ No specific URL for ${upperCountry}, using default${
          service ? ` (${service})` : ""
        }:`,
        countryConfig.default.substring(0, 50) + "...",
      );
      return countryConfig.default;
    }

    console.log(
      `[MuxVideoPlayerWithCTA] ❌ No CTA URL found for country: ${upperCountry}`,
    );
    return "";
  };

  // =============================================================================
  // PACK CONFIG API INTEGRATION (Feature Flag)
  // =============================================================================

  // Feature flag: enable/disable API-driven configuration
  const USE_API_CONFIG = import.meta.env.VITE_USE_PACK_CONFIG_API === "true";

  // Single source of truth for country: prefer explicit prop (e.g. from BioLinkTemplate/Worker) so Chile gets CL and correct $19.99
  const effectiveCountryForApi = countryCode ?? detectedCountry ?? "US";

  // Get pack from API if feature flag is enabled and service is provided
  // IMPORTANT: Always call hooks (React rules), use result conditionally
  const pack: PackConfig | null =
    USE_API_CONFIG && service ? (packs[service] as PackConfig) || null : null;

  // Resolve configuration from API with fallbacks
  // This always runs (React rules), but uses pack=null when flag is off → uses only fallbacks
  const apiVideoConfig = usePackConfigForVideo(
    pack,
    effectiveCountryForApi,
    getUserLanguage(),
    {
      // Fallback 1: Static configuration (telegramGroupsVideo)
      playbackIdFallback: (cc) => getTelegramPlaybackIdByCountry(cc),
      playbackIdHardcoded: "",
      ctaUrlFallback: (cc) => getTelegramCTAUrlByCountry(cc),
      ctaLabelFallback: (lang) => membershipVipCta(lang === "es" ? "es" : "en"),
    },
  );

  // =============================================================================
  // RESOLUTION PRIORITY (with API integration)
  // =============================================================================

  // Priority: manual props > API config (always for services) > country-specific > static-page config > service config > empty
  // When a non-empty playbackId is passed (e.g. /sorpresa, /exclusive), it is always used and nothing can override it.
  // If forceManualPlaybackId=true, ONLY use manual playbackId (no fallbacks from API/country/service)
  const playbackId =
    manualPlaybackId && manualPlaybackId.trim()
      ? manualPlaybackId.trim()
      : forceManualPlaybackId
        ? ""
        : (service ? apiVideoConfig.playbackId : null) ||
          getCountrySpecificPlaybackId() ||
          pageConfig?.playbackId ||
          serviceConfig?.playbackId ||
          "";
  let ctaUrl =
    manualCtaUrl ||
    (service ? apiVideoConfig.ctaUrl : null) ||
    getCountrySpecificCtaUrl() ||
    pageConfig?.ctaUrl ||
    serviceConfig?.ctaUrl ||
    "";
  let ctaLabel =
    manualCtaLabel ||
    (service ? apiVideoConfig.ctaLabel : null) ||
    pageConfig?.ctaLabel ||
    serviceConfig?.ctaLabel ||
    membershipVipCta(detectedLanguage === "es" ? "es" : "en");

  if (!skipWorkerTemplateCta) {
    const workerCta = resolveWorkerDrivenCta();
    if (workerCta.mode === "hidden") {
      ctaUrl = "";
    } else if (workerCta.mode === "worker") {
      ctaUrl = workerCta.url;
      ctaLabel = getWorkerCtaDisplayLabel(detectedLanguage, workerCta.variant);
    }
  }
  const title = manualTitle || pageConfig?.title || serviceConfig?.title;

  const muxFriendlyBanner = useMemo(
    () =>
      getMuxCtaFriendlyBannerMessage(
        detectedLanguage,
        {
          service,
          ctaSubtitle,
          staticPage: resolvedPage,
          premiumSimpleMembershipFreeFunnel,
        },
        muxFriendlyFunnel,
      ),
    [
      detectedLanguage,
      service,
      ctaSubtitle,
      resolvedPage,
      premiumSimpleMembershipFreeFunnel,
      muxFriendlyFunnel,
    ],
  );

  // Log API config usage (development only)
  useEffect(() => {
    if (import.meta.env.DEV && USE_API_CONFIG && service) {
      console.log("[MuxVideoPlayerWithCTA] API Config:", {
        enabled: USE_API_CONFIG,
        service,
        pack: pack?.id || "null",
        country: effectiveCountryForApi,
        isActive: apiVideoConfig.isActive,
        playbackId: apiVideoConfig.playbackId?.substring(0, 20) + "...",
        ctaUrl: apiVideoConfig.ctaUrl,
        ctaLabel: apiVideoConfig.ctaLabel,
        pricing: `${apiVideoConfig.priceFormatted} (${apiVideoConfig.currency})`,
      });
    }
  }, [USE_API_CONFIG, service, pack, effectiveCountryForApi, apiVideoConfig]);

  // Single source of truth for country in custom checkout (pricing + API body fallback + tracking)
  // Note: effectiveCountryForApi is defined above for API resolution
  const effectiveCountry = effectiveCountryForApi;

  const stripeWatcherLuckEligible = useMemo(() => {
    if (getWatcherLuckDiscountMaxPercentClient() <= 0) return false;
    if (!STRIPE_PUBLISHABLE_KEY) return false;
    if (!(service === "channel_invite" || Boolean(overridePackId)))
      return false;
    if (effectiveCountry === "AR" && effectivePackId === "channel_invite")
      return false;
    return true;
  }, [service, overridePackId, effectiveCountry, effectivePackId]);

  const stripeWatcherLuckPercent = useMemo(() => {
    if (!stripeWatcherLuckEligible) return 0;
    return getOrCreateSessionWatcherLuckPercent();
  }, [stripeWatcherLuckEligible]);

  // Check if content is active: only false when API explicitly returns isActive: false for this country.
  // When API has no entry for the country or fails, fallback config is used and content stays active.
  const isContentActive =
    USE_API_CONFIG && service ? apiVideoConfig.isActive : true;

  // Use fallback English playback ID when connection/CDN error occurred (PE, ES, etc.)
  const effectivePlaybackId = networkErrorFallbackPlaybackId || playbackId;

  // Mux Data: env key + metadata for every play → admin analytics (views / watch time)
  const muxDataEnvKey = getMuxDataEnvKey();
  const muxDataMetadata = useMemo(() => {
    if (!effectivePlaybackId?.trim()) return {};
    const path =
      typeof window !== "undefined" ? window.location.pathname || "/" : "/";
    const surface =
      path === "/" || path === ""
        ? "us_exclusive"
        : path.includes("reels")
          ? "reels"
          : path.includes("ser-miembro") || path.includes("membership")
            ? "dual_cta"
            : path.includes("premium") || path.includes("invitacion")
              ? "premium"
              : path.includes("canal") || path.includes("channel")
                ? "channel_invite"
                : "mux_cta";
    return buildMuxDataMetadata({
      playbackId: effectivePlaybackId,
      title: title || service || surface,
      surface,
      country: effectiveCountry,
      pagePath: path,
      template: service || slackFunnelTemplate || undefined,
    });
  }, [
    effectivePlaybackId,
    title,
    service,
    effectiveCountry,
    slackFunnelTemplate,
  ]);

  // Validate playbackId - must be non-empty and valid format
  const isValidPlaybackId =
    effectivePlaybackId &&
    effectivePlaybackId.trim().length > 0 &&
    effectivePlaybackId !== SAFE_PLAYBACK_ID_FALLBACK &&
    effectivePlaybackId.length >= 10; // Mux playback IDs are typically 20+ chars

  // Log playbackId validation on mount (only errors in production)
  useEffect(() => {
    // Only log in development or if there's an error
    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Component initialized:", {
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId?.substring(0, 20) + "...",
        isValid: isValidPlaybackId,
        source: manualPlaybackId
          ? "manual"
          : serviceConfig
            ? "service"
            : pageConfig
              ? "page"
              : "none",
      });
    }

    if (!isValidPlaybackId) {
      const errorMsg = !effectivePlaybackId
        ? "PlaybackId is empty - check env vars in Railway"
        : `Invalid playbackId: "${effectivePlaybackId}" (length: ${effectivePlaybackId.length})`;

      console.error(`[MuxVideoPlayerWithCTA] ❌ ${errorMsg}`);

      logVideoEvent({
        event: "playback_id_invalid",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId || "EMPTY",
        errorMessage: errorMsg,
      });
      trackMuxVideoFailureSlack({
        template: slackFunnelTemplate ?? inferSlackFunnelTemplateFromPath(pathname),
        errorType: !effectivePlaybackId ? "config_missing" : "playback_id_invalid",
        errorMessage: errorMsg,
        playbackId: effectivePlaybackId || undefined,
        country: effectiveCountry,
        service: service ?? undefined,
        muxEvent: "playback_id_invalid",
        ...slackFunnelContextRef.current,
      });
    }
  }, [
    isValidPlaybackId,
    effectivePlaybackId,
    effectiveCountry,
    service,
    manualPlaybackId,
    serviceConfig,
    pageConfig,
    slackFunnelTemplate,
    pathname,
  ]);
  const getFallbackPlaybackIdForErrors = useCallback((): string | null => {
    const isJoinFlow = ["telegram", "telegram_groups", "channel_invite"].includes(
      service ?? "",
    );
    if (isJoinFlow) return null;

    const fromPack = apiVideoConfig.fallbackPlaybackId?.trim();
    if (
      fromPack &&
      fromPack !== effectivePlaybackId &&
      fromPack.length >= 10
    ) {
      return fromPack;
    }

    if (forceManualPlaybackId || skipWorkerTemplateCta || manualPlaybackId) {
      return resolveMuxErrorFallbackPlaybackId(effectivePlaybackId);
    }

    return resolveMuxErrorFallbackPlaybackId(effectivePlaybackId);
  }, [
    service,
    apiVideoConfig.fallbackPlaybackId,
    effectivePlaybackId,
    forceManualPlaybackId,
    skipWorkerTemplateCta,
    manualPlaybackId,
  ]);

  // Reset fallback only when country explicitly changes (e.g. test_country) - not on initial detection
  const prevCountryRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevCountryRef.current;
    prevCountryRef.current = effectiveCountry;
    if (prev !== undefined && prev !== effectiveCountry) {
      setNetworkErrorFallbackPlaybackId(null);
      hasTriedFallbackRef.current = false;
    }
  }, [effectiveCountry]);

  // Early validation: if no playbackId (and no Spain fallback), show error
  if (!effectivePlaybackId || effectivePlaybackId.trim() === "") {
    logVideoErrorProduction({
      event: "video_error",
      country: effectiveCountry,
      service: service ?? undefined,
      errorType: "config_missing",
      errorMessage: "No playbackId provided",
    });
    console.error("[MuxVideoPlayerWithCTA] ❌ No playbackId provided", {
      manualPlaybackId,
      service,
      page: pageProp,
      pathname: location.pathname,
      countryCode,
    });
  }

  const showInstagramIcon = pageConfig?.showInstagramIcon ?? false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false); // Start as false, will be set to true when playing
  const [isMuted, setIsMuted] = useState(false); // Track mute state - try unmuted first
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loading state for video
  const [showPlayButton, setShowPlayButton] = useState(false); // Show play button if autoplay fails
  const [showMuteIndicator, setShowMuteIndicator] = useState(false); // Show mute indicator on mobile
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const MAX_RETRIES = 3; // Maximum number of retry attempts
  const [thumbnailError, setThumbnailError] = useState(false); // Track if thumbnail failed to load
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false); // Hide visual loading only after real playback starts
  const hasPlaybackStartedRef = useRef(false);
  const [hasReceivedMuxMetadata, setHasReceivedMuxMetadata] = useState(false);
  /** First decoded frame (`loadeddata`) — better perceived progress than manifest alone (Mux/HTML5). */
  const [muxHasLoadedData, setMuxHasLoadedData] = useState(false);
  /** Enough buffer to start playback (`canplay` / preload segments). */
  const [muxCanPlayBuffered, setMuxCanPlayBuffered] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0); // Real elapsed loading time
  const [networkTypeHint, setNetworkTypeHint] = useState("unknown");
  const [saveDataEnabled, setSaveDataEnabled] = useState(false);

  // Checkout state management
  const [checkoutState, setCheckoutState] =
    useState<CheckoutState>("VIDEO_PREVIEW");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);
  /** Full-screen "please wait" before hard navigations (router / checkout page / external URL). */
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const checkoutStateRef = useRef(checkoutState);
  checkoutStateRef.current = checkoutState;
  const muxStripeJsUnavailableTrackedRef = useRef(false);

  // Device detection hooks for platform-specific optimizations
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  const isAndroid = useIsAndroid();
  const muxDesktopMacMseHardening = useMuxDesktopMacMseHardening();
  const isLandscape = useIsLandscape();
  const isInstagram = isInstagramBrowser();
  /** MSE endurecido: Chromium en macOS (incl. Arc sin token en UA) + Arc explícito; no Instagram. */
  const muxArcPlaybackTune = muxDesktopMacMseHardening && !isInstagram;

  // For the main DualCTAVideoLanding membership video on mobile/IG:
  // always ensure playsinline + no native fullscreen to keep it inside our 9:16 / responsive frame.
  /** Menos presión sobre SourceBuffer de audio/vídeo (fallos appendBuffer en Chromium macOS). */
  const fragileMseHlsConfig: Partial<HlsConfig> | undefined = useMemo(
    () =>
      muxArcPlaybackTune
        ? {
            lowLatencyMode: false,
            maxBufferLength: 25,
            maxMaxBufferLength: 50,
            backBufferLength: 60,
            maxBufferHole: 0.5,
            nudgeOffset: 0.05,
            nudgeMaxRetry: 5,
            maxFragLookUpTolerance: 0.25,
            fragLoadingTimeOut: 20000,
          }
        : undefined,
    [muxArcPlaybackTune],
  );
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const parentCtaLockRef = useRef(false);
  const parentCtaUnlockTimerRef = useRef<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Instagram browser banner state (dismissible)
  const [showInstagramBanner, setShowInstagramBanner] = useState(false);
  const [instagramBannerDismissed, setInstagramBannerDismissed] =
    useState(false);
  const [linkCopiedForBrowser, setLinkCopiedForBrowser] = useState(false);

  /** Stripe-only fleeting toast: "lucky" bonus discount rolled for this tab session */
  const [showWatcherLuckBanner, setShowWatcherLuckBanner] = useState(false);
  const watcherLuckToastShownRef = useRef(false);
  const watcherLuckToastTimerRef = useRef<number | null>(null);
  const watcherLuckWallTimerRef = useRef<number | null>(null);
  const watcherLuckTUThrottleRef = useRef(0);

  // Show Instagram banner sooner (1.5s) when in embedded browser - helps users with half-screen/layout issues
  useEffect(() => {
    if (isInstagram && !instagramBannerDismissed) {
      const timer = setTimeout(() => {
        setShowInstagramBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isInstagram, instagramBannerDismissed]);

  // Apply Instagram browser CSS optimizations
  useEffect(() => {
    if (isInstagram) {
      applyInstagramBrowserCSS();
    }
  }, [isInstagram]);
  useEffect(() => {
    return () => {
      if (parentCtaUnlockTimerRef.current !== null) {
        clearTimeout(parentCtaUnlockTimerRef.current);
      }
      if (watcherLuckToastTimerRef.current !== null) {
        clearTimeout(watcherLuckToastTimerRef.current);
        watcherLuckToastTimerRef.current = null;
      }
      if (watcherLuckWallTimerRef.current !== null) {
        clearTimeout(watcherLuckWallTimerRef.current);
        watcherLuckWallTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (checkoutState === "VIDEO_PREVIEW") return;
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
  }, [checkoutState]);
  const safeCssId = sanitizePlaybackIdForCss(effectivePlaybackId ?? "");
  const ctaUrlValid = Boolean(ctaUrl && isAllowedRedirectUrl(ctaUrl));
  // Mostrar botón si hay URL válida O custom checkout (overridePackId + Stripe) para /sorpresa, /exclusive
  const showCTAButton =
    ctaUrlValid || (Boolean(overridePackId) && Boolean(STRIPE_PUBLISHABLE_KEY));
  const ctaBusy = isCreatingPaymentIntent || isNavigatingAway;

  // Debug: Log CTA configuration (solo en desarrollo)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[MuxVideoPlayerWithCTA] CTA Debug:", {
      service,
      ctaUrl,
      ctaUrlValid,
      ctaLabel,
      playbackId: playbackId?.substring(0, 20) + "...",
      serviceConfig: serviceConfig ? "loaded" : "not loaded",
      stripeKeyConfigured: Boolean(STRIPE_PUBLISHABLE_KEY),
    });
  }, [service, ctaUrl, ctaUrlValid, ctaLabel, playbackId, serviceConfig]);

  const isModal = variant === "modal";
  const effectiveDisplayMode = isModal ? "contain" : displayMode;
  const isLikelySlowNetwork =
    saveDataEnabled ||
    networkTypeHint === "slow-2g" ||
    networkTypeHint === "2g" ||
    networkTypeHint === "3g";
  const shouldShowSlowConnectionHint = loadingSeconds >= 4;

  const awaitingFirstPlayback =
    !hasPlaybackStarted &&
    !showPlayButton &&
    !isPlaying &&
    !hasError;
  /** Full-screen loader until we have stream metadata *and* a playable buffer (`canplay`). */
  const showHeavyVideoLoader =
    awaitingFirstPlayback &&
    isLoading &&
    (!hasReceivedMuxMetadata || !muxCanPlayBuffered);
  /** Minimal chrome once Mux reports `canplay` but autoplay has not emitted `playing` yet (common on mobile/WebView). */
  const showLightVideoLoader =
    awaitingFirstPlayback &&
    isLoading &&
    hasReceivedMuxMetadata &&
    muxCanPlayBuffered;

  const getDisplayMode = (): "reel" | "contain" | "cover" => {
    if (effectiveDisplayMode === "auto") return "reel";
    return effectiveDisplayMode as "reel" | "contain" | "cover";
  };

  const currentDisplayMode = getDisplayMode();
  const isReelMode = currentDisplayMode === "reel";
  const fillsParent =
    viewportScope === "container" ||
    /\bh-full\b/.test(containerClassName) ||
    /\bmin-h-0\b/.test(containerClassName);

  const [modalMounted, setModalMounted] = useState(!isModal);

  useEffect(() => {
    if (!isModal) return;
    const id = requestAnimationFrame(() => setModalMounted(true));
    return () => cancelAnimationFrame(id);
  }, [isModal]);

  useEffect(() => {
    if (!onClose) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    if (!onClose) return;
    const id = requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    hasPlaybackStartedRef.current = hasPlaybackStarted;
  }, [hasPlaybackStarted]);

  useEffect(() => {
    if (effectivePlaybackId) {
      setHasError(false);
      setErrorMessage("");
      setIsLoading(true); // Reset loading when playback ID changes (including Spain fallback)
      setHasReceivedMuxMetadata(false);
      setMuxHasLoadedData(false);
      setMuxCanPlayBuffered(false);
      setHasPlaybackStarted(false);
      hasPlaybackStartedRef.current = false;
      setShowPlayButton(false);
      setLoadingSeconds(0);
      setRetryCount(0);
      setThumbnailError(false);

      // NOTE: We intentionally do NOT preload the .m3u8 manifest here.
      // A <link rel="preload" as="fetch" crossOrigin="anonymous"> for the HLS manifest
      // is NEVER reused by Mux's hls.js player (different XHR CORS mode), so it:
      //  1. Triggers Chrome's "preloaded but not used" warning every time
      //  2. Can cause a CORS cache-poisoning that makes the player emit a mediaError
      // The Mux player handles its own manifest fetch efficiently; no preload needed.

      // Keep loading state long enough to avoid exposing a static/non-playing frame too early.
      // It will be hidden earlier by real playback events (canplay/play).
      const maxLoadingTime = isMobile || isInstagram ? 6000 : 8000;
      const maxLoadingTimeout = setTimeout(() => {
        if (!hasPlaybackStartedRef.current) {
          setIsLoading(false);
          setShowPlayButton(true);
          signalNeedsUserGesture();
          logVideoEvent({
            event: "video_shown_thumbnail_only",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
          });
        }
      }, maxLoadingTime);

      return () => {
        clearTimeout(maxLoadingTimeout);
      };
    }
  }, [
    effectivePlaybackId,
    isMobile,
    isInstagram,
    effectiveCountry,
    service,
    signalNeedsUserGesture,
  ]);

  // Network quality hint for user-facing loading messages (best effort)
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    };
    const connection = nav.connection;
    if (!connection) return;
    if (connection.effectiveType) setNetworkTypeHint(connection.effectiveType);
    setSaveDataEnabled(Boolean(connection.saveData));
  }, []);

  // Track real loading duration so user sees progress, not a static wait (desde entrada hasta listo / play)
  useEffect(() => {
    const inInitialMuxLoad = awaitingFirstPlayback && isLoading;
    if (!inInitialMuxLoad || hasPlaybackStarted) {
      setLoadingSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setLoadingSeconds(0);
    const timer = window.setInterval(() => {
      setLoadingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [
    awaitingFirstPlayback,
    isLoading,
    hasPlaybackStarted,
    effectivePlaybackId,
  ]);

  const handleError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      try {
        let msg = "";
        let type = "";
        let details = "";
        let url: string | undefined;
        let fatal = false;
        if (error && typeof error === "object" && "message" in error) {
          msg = String((error as { message?: string }).message ?? "");
          type = String((error as { type?: string }).type ?? "");
          details = String((error as { details?: string }).details ?? "");
          url = (error as { url?: string }).url;
          fatal = Boolean((error as { fatal?: boolean }).fatal);
        }

        const lowerMsg = msg.toLowerCase();
        const lowerDetails = details.toLowerCase();
        const isBufferAppendMux =
          lowerMsg.includes("bufferappend") ||
          lowerDetails.includes("bufferappend") ||
          lowerMsg.includes("sourcebuffer") ||
          lowerMsg.includes("appendbuffer") ||
          lowerMsg.includes("removed from the parent media source") ||
          lowerMsg.includes("invalidstateerror");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (Code 4) / "no supported sources" — load() no ayuda.
        const isNoSourceMux =
          lowerDetails.includes("code 4") ||
          lowerMsg.includes("no supported sources");

        // Ignore errors that are not related to Mux video
        // Check if error is from a blocked resource (like ad blockers blocking trackers)
        if (typeof url === "string") {
          // Ignore errors from non-Mux domains (like metricool, analytics, etc.)
          if (!url.includes("mux.com") && !url.includes("stream.mux.com")) {
            console.debug(
              "[MuxVideoPlayerWithCTA] Ignoring non-Mux error:",
              url,
            );
            return;
          }
        }

        // Only handle errors that are clearly video-related
        const isVideoError =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          msg.toLowerCase().includes("video") ||
          msg.toLowerCase().includes("playback") ||
          msg.toLowerCase().includes("stream") ||
          msg.toLowerCase().includes("mux") ||
          msg.toLowerCase().includes("manifest") ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("token") ||
          type === "mediaError" ||
          type === "networkError" ||
          details === "manifestLoadError" ||
          (typeof url === "string" &&
            (url.includes("stream.mux.com") || url.includes("mux.com")));

        // channel_invite = telegram groups, principal. NUNCA bloquear CTA.
        const isJoinFlowService = [
          "telegram",
          "telegram_groups",
          "channel_invite",
        ].includes(service ?? "");

        // If it's not a video error, ignore it
        if (!isVideoError) {
          console.debug("[MuxVideoPlayerWithCTA] Ignoring non-video error:", {
            msg,
            type,
            details,
            url,
          });
          return;
        }

        // Check if it's a network error that might be retryable
        const isNetworkError =
          type === "networkError" ||
          details === "manifestLoadError" ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("connection");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (code 4): no valid source — permanent, never retry.
        // "The element has no supported sources" = wrong/missing playback ID.
        const isNoSourceError =
          details.toLowerCase().startsWith("code 4") ||
          msg.toLowerCase().includes("no supported sources") ||
          msg.toLowerCase().includes("src_not_supported");

        // Decode/parse errors (e.g. demuxer_error_could_not_parse, MEDIA_ERR_DECODE)
        // Often transient MSE/CDN issues; retrying player.load() commonly recovers.
        const isDecodeError =
          !isNoSourceError && (
            msg.toLowerCase().includes("demuxer") ||
            msg.toLowerCase().includes("could_not_parse") ||
            msg.toLowerCase().includes("decode") ||
            msg.toLowerCase().includes("not supported") ||
            details.toLowerCase().includes("demuxer") ||
            details.toLowerCase().includes("decode") ||
            details.toLowerCase().includes("parse")
          );

        const isInvalid =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          (typeof url === "string" &&
            url.includes("stream.mux.com") &&
            url.includes("404")) ||
          isNoSourceMux;

        // Join flow: priorizar fallback a inglés antes que retries (siempre debe haber video)
        // Si no es join flow y es network/decode error, retry primero
        if (
          !isJoinFlowService &&
          (isNetworkError || isDecodeError) &&
          !isNoSourceError &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          !fatal
        ) {
          console.log(
            `[MuxVideoPlayerWithCTA] ${isDecodeError ? "Decode" : "Network"} error detected, retrying (${
              retryCount + 1
            }/${MAX_RETRIES})...`,
          );
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);

          // Retry after a delay
          setTimeout(
            () => {
              const player = playerRef.current;
              if (player && typeof player.load === "function") {
                player.load();
              }
            },
            1000 * (retryCount + 1),
          ); // Exponential backoff
          return;
        }

        // Recoverable MSE/HLS errors (p. ej. bufferAppendError): un load() suele bastar.
        // hls.js marca algunos como fatal; igual permitimos reintentos acotados.
        if (
          !isJoinFlowService &&
          type.toLowerCase() === "mediaerror" &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          (!fatal || isBufferAppendMux)
        ) {
          console.warn(
            `[MuxVideoPlayerWithCTA] Recoverable mediaError, silent reload (${retryCount + 1}/${MAX_RETRIES}):`,
            msg.slice(0, 120),
          );
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);
          setTimeout(() => {
            const player = playerRef.current;
            if (player && typeof player.load === "function") {
              player.load();
            }
          }, 500);
          return;
        }

        // Log producción: error de video detectado
        const normalizedMsg = msg.toLowerCase();
        const normalizedDetails = details.toLowerCase();
        const isTokenError =
          normalizedMsg.includes("token") ||
          normalizedMsg.includes("unauthorized") ||
          normalizedMsg.includes("forbidden") ||
          normalizedMsg.includes("jwt") ||
          normalizedMsg.includes("signature") ||
          normalizedMsg.includes("access denied") ||
          normalizedMsg.includes("401") ||
          normalizedMsg.includes("403") ||
          normalizedDetails.includes("token") ||
          normalizedDetails.includes("unauthorized") ||
          normalizedDetails.includes("forbidden");
        logVideoErrorProduction({
          event: "video_error",
          country: effectiveCountry,
          service: service ?? undefined,
          errorType: type || (isNetworkError ? "network" : "other"),
          errorMessage: msg.slice(0, 200),
          playbackId: effectivePlaybackId,
          isNetworkError,
          isTokenError,
        });

        trackMuxVideoFailureSlack({
          template: slackFunnelTemplate ?? inferSlackFunnelTemplateFromPath(pathname),
          errorType:
            isTokenError
              ? "token"
              : isNetworkError
                ? "network"
                : isDecodeError
                  ? "decode"
                  : isInvalid
                    ? "playback_id_invalid"
                    : type || "other",
          errorMessage: msg.slice(0, 200) || type || "video_error",
          playbackId: effectivePlaybackId,
          country: effectiveCountry,
          service: service ?? undefined,
          muxEvent: "video_error",
          videoStarted: slackFunnelContextRef.current?.videoStarted ?? hasPlaybackStartedRef.current,
          videoCompleted: slackFunnelContextRef.current?.videoCompleted,
          ctaVipClicked: slackFunnelContextRef.current?.ctaVipClicked,
          ctaFreeClicked: slackFunnelContextRef.current?.ctaFreeClicked,
          checkoutOpened:
            slackFunnelContextRef.current?.checkoutOpened ??
            checkoutStateRef.current !== "VIDEO_PREVIEW",
          timeOnPageSeconds: slackFunnelContextRef.current?.timeOnPageSeconds,
          dualVariantId: slackFunnelContextRef.current?.dualVariantId,
          dualVariantGeoScope: slackFunnelContextRef.current?.dualVariantGeoScope,
          dualVariantGeoCode: slackFunnelContextRef.current?.dualVariantGeoCode,
          isNetworkError,
          isTokenError,
        });

        // Notify parent for Slack/analytics — best-effort, never throw
        try {
          onErrorRef.current?.(msg.slice(0, 200) || type || "video_error");
        } catch { /* */ }

        // channel_invite (telegram groups): strict mode - do not fallback or auto-continue.
        // Show blocking frontend error so user is not redirected elsewhere.
        if (isJoinFlowService) {
          logVideoErrorProduction({
            event: "fallback_failed",
            country: effectiveCountry,
            service: service ?? undefined,
            errorMessage: "strict_mode_no_mux_fallback",
            playbackId: effectivePlaybackId,
            fallbackAttempted: false,
          });
          setHasError(true);
          setIsLoading(false);
          setErrorMessage(
            "El video no está disponible en este momento. Volvé a intentar más tarde.",
          );
          return;
        }

        // Para network/token/decode errors (no join flow): intentar fallback si aplica
        if ((isNetworkError || isTokenError || isDecodeError) && !isInvalid) {
          const fallbackPlaybackId = getFallbackPlaybackIdForErrors();
          if (
            fallbackPlaybackId &&
            fallbackPlaybackId.trim() !== "" &&
            !hasTriedFallbackRef.current
          ) {
            logVideoErrorProduction({
              event: "fallback_attempted",
              country: effectiveCountry,
              service: service ?? undefined,
              errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
              fallbackAttempted: true,
            });
            hasTriedFallbackRef.current = true;
            setNetworkErrorFallbackPlaybackId(fallbackPlaybackId);
            setIsLoading(false);
            setErrorMessage("");
            return;
          }
          // Sin fallback disponible (caso típico: manualPlaybackId standalone como VIP/sorpresa).
          // No bloquear la UI: el thumbnail y el CTA siguen visibles para no perder conversión.
          logVideoErrorProduction({
            event: "fallback_failed",
            country: effectiveCountry,
            service: service ?? undefined,
            errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
            errorMessage: "no fallback playback ID available; keeping CTA visible",
            playbackId: effectivePlaybackId,
            fallbackAttempted: false,
          });
          setIsLoading(false);
          setErrorMessage("");
          setHasError(false);
          setShowPlayButton(true);
          signalNeedsUserGesture();
          return;
        }

        // Membership / manual playback: nunca pantalla blocking — tap-to-play + CTA del template.
        if (forceManualPlaybackId || skipWorkerTemplateCta) {
          setHasError(false);
          setIsLoading(false);
          setErrorMessage("");
          setShowPlayButton(true);
          signalNeedsUserGesture();
          return;
        }

        // Solo mostrar error blocking para otros servicios (no join flow)
        setHasError(true);
        setIsLoading(false);
        setErrorMessage(
          isInvalid
            ? "This video is not available. The playback ID may be invalid or the video has been removed."
            : "Video could not be loaded. Please try again later.",
        );
      } catch (err) {
        console.error("[MuxVideoPlayerWithCTA] Error in handleError:", err);
        // Don't set error state for unexpected errors in error handler
        // This prevents cascading errors from breaking the component
      }
    },
    [
      retryCount,
      service,
      effectiveCountry,
      effectivePlaybackId,
      getFallbackPlaybackIdForErrors,
      forceManualPlaybackId,
      skipWorkerTemplateCta,
      signalNeedsUserGesture,
      slackFunnelTemplate,
      pathname,
    ],
  );

  /** Mux onError puede ser CustomEvent: `detail` es un objeto con type/details/fatal, no un string. */
  function extractMuxPlayerErrorPayload(
    error: unknown,
  ): {
    message: string;
    type: string;
    details: string;
    url?: string;
    fatal?: boolean;
  } | null {
    if (!error || typeof error !== "object") return null;
    const e = error as Record<string, unknown>;
    if (typeof e.detail === "string" && e.detail.trim() !== "") {
      const s = e.detail.trim();
      return { message: s, type: "mediaError", details: s, fatal: false };
    }
    const raw =
      e.detail && typeof e.detail === "object"
        ? (e.detail as Record<string, unknown>)
        : e;
    const message = String(raw.message ?? "");
    const type = String(raw.type ?? "");
    const details = String(raw.details ?? "");
    const url = typeof raw.url === "string" ? raw.url : undefined;
    const fatal = Boolean(raw.fatal);
    if (!message && !type && !details && !url) return null;
    return { message, type, details, url, fatal };
  }

  // Wrapper for MuxPlayer onError to validate it's a video error
  const handleMuxPlayerError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      if (!error) {
        return;
      }

      const payload = extractMuxPlayerErrorPayload(error);

      let errorStr = "";
      try {
        errorStr = JSON.stringify(error).toLowerCase();
      } catch {
        errorStr = String(error).toLowerCase();
      }

      const combinedStr = payload
        ? `${errorStr} ${payload.message} ${payload.type} ${payload.details} ${payload.url ?? ""}`.toLowerCase()
        : errorStr;

      const hasVideoIndicators =
        combinedStr.includes("video") ||
        combinedStr.includes("playback") ||
        combinedStr.includes("stream") ||
        combinedStr.includes("mux") ||
        combinedStr.includes("manifest") ||
        combinedStr.includes("network") ||
        combinedStr.includes("tunnel") ||
        combinedStr.includes("connection") ||
        combinedStr.includes("mediaerror") ||
        combinedStr.includes("invalid") ||
        combinedStr.includes("not supported") ||
        combinedStr.includes("bufferappend") ||
        (payload?.type === "mediaError" || payload?.type === "networkError");

      if (!hasVideoIndicators) {
        console.debug(
          "[MuxVideoPlayerWithCTA] Ignoring non-video error from MuxPlayer:",
          error,
        );
        return;
      }

      if (payload) {
        handleError(payload);
      } else {
        handleError(error);
      }
    },
    [handleError],
  );

  useEffect(() => {
    if (!effectivePlaybackId) return;

    const onVideoError = (event: Event) => {
      const target = event.target as HTMLVideoElement;
      // Only handle errors from actual video elements
      if (!target || target.tagName !== "VIDEO") {
        return;
      }

      const err = target.error;
      if (err && err.code !== undefined) {
        // Only handle actual video errors (MEDIA_ERR_* codes)
        // MEDIA_ERR_ABORTED = 1, MEDIA_ERR_NETWORK = 2, MEDIA_ERR_DECODE = 3, MEDIA_ERR_SRC_NOT_SUPPORTED = 4
        if (err.code >= 1 && err.code <= 4) {
          const errorDetails = {
            message: err.message || `Video error code ${err.code}`,
            type: "mediaError",
            details: `Code ${err.code}: ${
              err.message || "Unknown video error"
            }`,
            url: target.src || target.currentSrc,
          };

          // Log detailed error info
          logVideoEvent({
            event: err.code === 2 ? "network_error" : "video_load_failed",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "mediaError",
            errorMessage: err.message || `Video error code ${err.code}`,
            errorCode: err.code,
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: err.code === 2,
          });

          handleError(errorDetails);
        }
      } else {
        // Only handle network errors if they're from Mux domains
        const src = target.src || target.currentSrc || "";
        if (src.includes("mux.com") || src.includes("stream.mux.com")) {
          // Log network error
          logVideoEvent({
            event: "network_error",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "networkError",
            errorMessage: "Video could not be loaded",
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: true,
          });

          handleError({
            type: "networkError",
            message: "Video could not be loaded",
            details: "Network or loading error",
            url: src,
          });
        }
      }
    };

    const setup = (): (() => void) | undefined => {
      const p = playerRef.current;
      if (!p) return undefined;
      let el: HTMLVideoElement | null = null;
      if (
        typeof (p as { querySelector?: (s: string) => HTMLElement | null })
          .querySelector === "function"
      ) {
        el = (
          p as { querySelector: (s: string) => HTMLVideoElement | null }
        ).querySelector("video");
      }
      if (!el && (p as { shadowRoot?: ShadowRoot }).shadowRoot) {
        el = (p as { shadowRoot: ShadowRoot }).shadowRoot.querySelector(
          "video",
        );
      }
      if (!el && (p as { media?: HTMLVideoElement }).media) {
        el = (p as { media: HTMLVideoElement }).media;
      }
      if (el) {
        el.addEventListener("error", onVideoError);
        return () => el?.removeEventListener("error", onVideoError);
      }
      return undefined;
    };

    let cleanup2: (() => void) | undefined;
    const cleanup1 = setup();
    const t = setTimeout(() => {
      cleanup2 = setup();
    }, 500);
    return () => {
      cleanup1?.();
      cleanup2?.();
      clearTimeout(t);
    };
  }, [effectivePlaybackId, handleError, effectiveCountry, service]);

  // Helper function to get video element from player
  const getVideoElement = useCallback(
    (
      player: {
        querySelector?: (s: string) => HTMLElement | null;
        shadowRoot?: ShadowRoot;
        media?: HTMLVideoElement;
      } | null,
    ): HTMLVideoElement | null => {
      if (!player) return null;

      // Try different methods to get the video element
      if (typeof player.querySelector === "function") {
        const el = player.querySelector("video") as HTMLVideoElement | null;
        if (el) return el;
      }

      if (player.shadowRoot) {
        const el = player.shadowRoot.querySelector("video");
        if (el) return el;
      }

      if (player.media) {
        return player.media;
      }

      return null;
    },
    [],
  );

  const revealWatcherLuckToast = useCallback(() => {
    if (
      !stripeWatcherLuckEligible ||
      stripeWatcherLuckPercent <= 0 ||
      watcherLuckToastShownRef.current
    ) {
      return;
    }
    if (checkoutStateRef.current !== "VIDEO_PREVIEW") return;

    watcherLuckToastShownRef.current = true;

    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }

    setShowWatcherLuckBanner(true);
    if (watcherLuckToastTimerRef.current !== null) {
      window.clearTimeout(watcherLuckToastTimerRef.current);
    }
    watcherLuckToastTimerRef.current = window.setTimeout(() => {
      setShowWatcherLuckBanner(false);
      watcherLuckToastTimerRef.current = null;
    }, WATCHER_LUCK_TOAST_MS);
  }, [stripeWatcherLuckEligible, stripeWatcherLuckPercent]);

  const onPlaybackTelemetryRef = useRef(onPlaybackTelemetry);
  useEffect(() => {
    onPlaybackTelemetryRef.current = onPlaybackTelemetry;
  }, [onPlaybackTelemetry]);

  const playbackTelemetryThrottleRef = useRef(0);

  const emitPlaybackTelemetry = useCallback(
    (reason: "timeupdate" | "pause" | "ended") => {
      const cb = onPlaybackTelemetryRef.current;
      if (!cb) return;
      const now = Date.now();
      if (reason === "timeupdate") {
        if (now - playbackTelemetryThrottleRef.current < 4000) return;
        playbackTelemetryThrottleRef.current = now;
      }
      const p = playerRef.current;
      if (!p) return;
      let currentTimeSeconds = 0;
      let durationSeconds: number | null = null;
      const v = getVideoElement(p);
      if (v) {
        const ct = Number(v.currentTime);
        const d = Number(v.duration);
        currentTimeSeconds = Number.isFinite(ct) ? ct : 0;
        durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
      } else {
        const ct = (p as { currentTime?: number }).currentTime;
        const d = (p as { duration?: number }).duration;
        currentTimeSeconds =
          typeof ct === "number" && Number.isFinite(ct) ? ct : 0;
        durationSeconds =
          typeof d === "number" && Number.isFinite(d) && d > 0 ? d : null;
      }
      cb({
        currentTimeSeconds,
        durationSeconds,
        playbackId: effectivePlaybackId,
        reason,
      });
    },
    [getVideoElement, effectivePlaybackId],
  );

  const handleTimeUpdateForTelemetry = useCallback(() => {
    emitPlaybackTelemetry("timeupdate");
    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current
    ) {
      const now = Date.now();
      if (now - watcherLuckTUThrottleRef.current < 650) return;
      watcherLuckTUThrottleRef.current = now;
      try {
        const p = playerRef.current;
        if (!p) return;
        let ct = 0;
        const v = getVideoElement(p);
        if (v && Number.isFinite(v.currentTime)) {
          ct = v.currentTime;
        } else {
          const raw = (p as { currentTime?: number }).currentTime;
          ct = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
        }
        if (ct >= WATCHER_LUCK_MIN_PLAYHEAD_SEC) {
          revealWatcherLuckToast();
        }
      } catch {
        /* ignore */
      }
    }
  }, [
    emitPlaybackTelemetry,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    getVideoElement,
    revealWatcherLuckToast,
  ]);

  const handleCanPlay = useCallback(() => {
    setMuxCanPlayBuffered(true);
    console.log("[MuxVideoPlayerWithCTA] Video can play - setting volume");

    // Log successful video load
    logVideoEvent({
      event: "video_load_success",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });

    // Set volume when video can play (before it starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
        // No cerramos el loading aquí: el overlay sigue hasta `play` (o botón manual / timeout).
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in canplay:", error);
    }
  }, [getVideoElement, effectiveCountry, service, effectivePlaybackId]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setHasPlaybackStarted(true);
    setIsLoading(false); // Hide loading when video starts playing
    setShowPlayButton(false); // Hide play button when video starts playing

    // Log successful video play
    logVideoEvent({
      event: "video_play_success",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });

    // Set volume when video starts playing (safe - video is already playing)
    try {
      const p = playerRef.current;
      if (p) {
        const videoElement = getVideoElement(p);
        if (videoElement && "volume" in videoElement) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      // Non-critical error, just log it
      console.debug(
        "[MuxVideoPlayerWithCTA] Error setting volume on play:",
        error,
      );
    }

    console.log("[MuxVideoPlayerWithCTA] Video is playing");
    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current &&
      watcherLuckWallTimerRef.current === null
    ) {
      watcherLuckWallTimerRef.current = window.setTimeout(() => {
        watcherLuckWallTimerRef.current = null;
        revealWatcherLuckToast();
      }, WATCHER_LUCK_FALLBACK_WALL_MS);
    }
    onPlay?.();
  }, [
    onPlay,
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    revealWatcherLuckToast,
  ]);

  const dismissWatcherLuckBanner = useCallback(() => {
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    emitPlaybackTelemetry("pause");
  }, [emitPlaybackTelemetry]);

  // Handler for manual play button click - Always try with sound
  const handleManualPlay = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;

      const videoElement = getVideoElement(player);
      if (videoElement) {
        // Unmute and set volume when user manually plays
        videoElement.muted = false;
        if ("volume" in videoElement) {
          videoElement.volume = 1.0;
        }
        setIsMuted(false);
      }

      if (typeof player.play === "function") {
        player
          .play()
          .then(() => {
            setIsPlaying(true);
            setShowPlayButton(false);
            setShowMuteIndicator(false);
            console.log(
              "[MuxVideoPlayerWithCTA] Manual play with sound succeeded",
            );
          })
          .catch((error) => {
            console.warn("[MuxVideoPlayerWithCTA] Manual play failed:", error);
            // If fails, try muted
            if (videoElement) {
              videoElement.muted = true;
              setIsMuted(true);
              player
                .play()
                .then(() => {
                  setIsPlaying(true);
                  setShowPlayButton(false);
                  setShowMuteIndicator(true);
                })
                .catch(() => {
                  console.error(
                    "[MuxVideoPlayerWithCTA] Muted play also failed",
                  );
                });
            }
          });
      }
    } catch (error) {
      console.error(
        "[MuxVideoPlayerWithCTA] Error in handleManualPlay:",
        error,
      );
    }
  }, [getVideoElement]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    emitPlaybackTelemetry("ended");
    onEnded?.();
  }, [onEnded, emitPlaybackTelemetry]);

  // Toggle mute/unmute - Instagram-like tap to unmute on mobile
  const toggleMute = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;

      const videoElement = getVideoElement(player);
      if (videoElement) {
        const newMutedState = !isMuted;
        videoElement.muted = newMutedState;
        setIsMuted(newMutedState);

        // If unmuting, set volume to 100%
        if (!newMutedState && "volume" in videoElement) {
          videoElement.volume = 1.0;
        }

        console.log(
          `[MuxVideoPlayerWithCTA] ${newMutedState ? "Muted" : "Unmuted"} video`,
        );
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in toggleMute:", error);
    }
  }, [isMuted, getVideoElement]);

  const togglePlayPause = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;
      if (
        typeof player.play !== "function" ||
        typeof player.pause !== "function"
      )
        return;
      if (isPlaying) {
        player.pause();
        setIsPlaying(false);
      } else {
        player.play().catch((error) => {
          console.debug(
            "[MuxVideoPlayerWithCTA] Play failed in toggle:",
            error,
          );
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in togglePlayPause:", error);
    }
  }, [isPlaying]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const touches = e.changedTouches;
      if (!touches?.length) {
        touchStartRef.current = null;
        return;
      }
      const t = touches[0];
      const target = document.elementFromPoint(
        t.clientX,
        t.clientY,
      ) as HTMLElement | null;
      if (
        target?.closest(".action-button-container") ||
        target?.closest(".mux-cta-close-btn")
      ) {
        touchStartRef.current = null;
        return;
      }
      const dx = Math.abs(t.clientX - touchStartRef.current.x);
      const dy = Math.abs(t.clientY - touchStartRef.current.y);
      const dt = Date.now() - touchStartRef.current.time;
      touchStartRef.current = null;

      // Only respond to taps (small movement, short duration)
      if (dx < 10 && dy < 10 && dt < 300) {
        // If video is playing: tap to toggle mute
        // This works for all devices (mobile, desktop, Instagram)
        if (isPlaying) {
          toggleMute();
        } else {
          togglePlayPause();
        }
      }
    },
    [togglePlayPause, toggleMute, isPlaying],
  );

  const handleLoadedMetadata = useCallback(() => {
    setHasReceivedMuxMetadata(true);
    // Log metadata loaded (video info received)
    logVideoEvent({
      event: "video_load_start",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });
    onReady?.();
    console.log("[MuxVideoPlayerWithCTA] Metadata loaded - setting volume");

    // Set volume when metadata loads (before playback starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in loadedMetadata:", error);
    }
  }, [
    onReady,
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
  ]);

  const handleMuxLoadedData = useCallback(() => {
    setMuxHasLoadedData(true);
  }, []);

  // Show mute indicator when video is playing and muted
  // Keep visible until user unmutes (don't auto-hide)
  useEffect(() => {
    if (isPlaying && isMuted) {
      setShowMuteIndicator(true);
    } else {
      setShowMuteIndicator(false);
    }
  }, [isPlaying, isMuted]);

  // Handle autoplay — respeta `autoPlay` prop (any | muted | false).
  const isMobileOrInstagram = isMobile || isInstagram;

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    if (autoPlay === false) {
      setIsLoading(false);
      setShowPlayButton(true);
      signalNeedsUserGesture();
      return;
    }

    let autoplayAttempted = false;

    const tryMutedAutoplay = async (): Promise<boolean> => {
      const videoElement = getVideoElement(player);
      if (videoElement) {
        videoElement.muted = true;
        setIsMuted(true);
      }
      if (typeof player.play !== "function") return false;
      await player.play();
      setShowPlayButton(false);
      setShowMuteIndicator(true);
      console.log(
        "[MuxVideoPlayerWithCTA] Autoplay muted succeeded - showing audio prompt",
      );

      // Best-effort: immediately try to unmute after the video has started playing.
      // Some browsers allow unmuting once playback has begun (even if initial autoplay was muted).
      // This helps the video "start with sound always" when possible.
      const videoEl = getVideoElement(player);
      setTimeout(() => {
        if (videoEl && videoEl.muted) {
          try {
            videoEl.muted = false;
            if ("volume" in videoEl) (videoEl as any).volume = 1.0;
            setIsMuted(false);
            setShowMuteIndicator(false);
            console.log("[MuxVideoPlayerWithCTA] Post-play unmute attempt succeeded");
          } catch (e) {
            console.debug("[MuxVideoPlayerWithCTA] Post-play unmute failed (expected in some browsers)");
          }
        }
      }, 80);

      return true;
    };

    const tryPlay = async () => {
      if (autoplayAttempted) return;
      autoplayAttempted = true;

      if (autoPlay === "muted") {
        try {
          await tryMutedAutoplay();
          return;
        } catch (mutedError) {
          console.debug(
            "[MuxVideoPlayerWithCTA] Muted autoplay failed:",
            mutedError,
          );
          setIsLoading(false);
          setShowPlayButton(true);
          signalNeedsUserGesture();
          return;
        }
      }

      try {
        const videoElement = getVideoElement(player);

        // Step 1: Try playing with sound (unmuted) — solo autoPlay="any"
        if (videoElement) {
          videoElement.muted = false;
          if ("volume" in videoElement) videoElement.volume = 1.0;
        }

        if (typeof player.play === "function") {
          await player.play();
          setShowPlayButton(false);
          setIsMuted(false);
          setShowMuteIndicator(false);
          console.log("[MuxVideoPlayerWithCTA] Autoplay with sound succeeded");

          // Extra: ensure volume and try unmute again shortly after (helps some browsers)
          const v = getVideoElement(player);
          setTimeout(() => {
            if (v) {
              v.muted = false;
              if ("volume" in v) (v as any).volume = 1;
            }
          }, 120);
          return;
        }
      } catch (error) {
        console.debug(
          "[MuxVideoPlayerWithCTA] Autoplay with sound failed:",
          error,
        );

        // Step 2: Fallback to muted autoplay
        try {
          await tryMutedAutoplay();
          return;
        } catch (mutedError) {
          console.debug(
            "[MuxVideoPlayerWithCTA] Muted autoplay also failed:",
            mutedError,
          );
          setIsLoading(false);
          setShowPlayButton(true);
          signalNeedsUserGesture();
        }
      }
    };

    const initialDelay = 50;
    const timeout = setTimeout(() => tryPlay(), initialDelay);

    // Check if playing after delay
    const checkDelay = isMobileOrInstagram ? 1500 : 2000;
    const checkPlaybackTimeout = setTimeout(() => {
      const videoElement = getVideoElement(player);
      if (videoElement && videoElement.paused) {
        setIsLoading(false);
        setShowPlayButton(true);
        signalNeedsUserGesture();
      }
    }, checkDelay);

    return () => {
      clearTimeout(timeout);
      clearTimeout(checkPlaybackTimeout);
    };
  }, [
    playbackId,
    isLoading,
    getVideoElement,
    isMobileOrInstagram,
    autoPlay,
    signalNeedsUserGesture,
  ]);

  // Create payment intent for checkout (uses effectiveCountry for pricing; API prefers server IP for metadata)
  const createPaymentIntent = useCallback(
    async (packId: string, email?: string) => {
      setCheckoutError(null);

      // ── ARGENTINA: Validar pack por pack con su propio MercadoPago ──
      if (effectiveCountry === "AR") {
        // Pack Basic (channel_invite) → MercadoPago Basic (link prebuilt con precio ARS en MP).
        if (packId === "channel_invite") {
          const mpUrl = getMercadoPagoCheckoutBasicArUrl();
          if (mpUrl) {
            flushSync(() => setIsNavigatingAway(true));
            window.location.assign(mpUrl);
            return;
          }

          // Sin URL de MP configurada: en vez de dejar al usuario sin checkout,
          // caemos a Stripe suscripción (USD) — mismo criterio que el flujo VIP.
          // (channel_invite es suscripción premium: el payment-intent one-time lo
          // rechaza con PREMIUM_SUBSCRIPTION_REQUIRED, por eso usamos create-subscription-checkout.)
          try {
            const pricing =
              getPackPricing(packId, effectiveCountry) ??
              getPackPricingStatic(packId, effectiveCountry);
            const amountCents = pricing?.amount ?? 1499;
            const base = window.location.origin;
            const datafastMeta = getDataFastStripeMetadata();
            // MRR subscription (Stripe fallback when MP Basic URL missing).
            trackDataFastGoal("initiate_checkout", {
              flow: "mux_video_cta",
              plan_tier: "basic",
              pack_id: packId,
              currency: "usd",
              amount_cents: String(amountCents),
              gateway: "stripe",
              country: "AR",
            });
            const { ok: stripeOk, data: stripeData } = await fetchCheckoutJson<{
              url?: string;
              error?: string;
            }>(`${base}/api/create-subscription-checkout`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                amount: amountCents,
                currency: "usd",
                productName: PACKS.channel_invite?.nameEs || "Invitación Canal",
                country: "AR",
                successUrl: `${base}/registro?plan_tier=basic`,
                cancelUrl: window.location.href,
                ...datafastMeta,
                metadata: {
                  plan_tier: "basic",
                  flow: "mux_video_cta",
                  pack_id: packId,
                  source: window.location.pathname,
                  country: "AR",
                  ...datafastMeta,
                },
                // HeroCTA + template + attribution del primer pageview
                // (referrer / landingUrl con UTM). getHeroCtaSlackExtra ya
                // mergea ambas — el Slack alert ve la ruta completa.
                ...getHeroCtaSlackExtra(),
              }),
            });
            if (stripeOk && stripeData.url) {
              flushSync(() => setIsNavigatingAway(true));
              window.location.assign(stripeData.url);
              return;
            }
            throw new Error(stripeData.error || "stripe_subscription_unavailable");
          } catch (stripeErr) {
            trackStripePayFailurePostHog({
              stage: "checkout_session_create",
              flow_surface: "mux_video_cta",
              failure_kind: "mp_basic_ar_stripe_fallback_failed",
              pack_id: packId,
              error_message: stripeErr instanceof Error ? stripeErr.message : String(stripeErr),
            });
            setCheckoutError("Checkout no disponible en este momento. Contacta a soporte.");
            setCheckoutState("ERROR");
            setIsCreatingPaymentIntent(false);
            return;
          }
        }

        // Para otros packs de Argentina: mostrar mensaje específico
        // No hay fallback genérico - cada pack necesita su propia configuración
        setCheckoutError(
          `El pack "${packId}" no está disponible para Argentina en este momento. ` +
          `Solo el plan Basic está disponible con MercadoPago.`
        );
        setCheckoutState("ERROR");
        trackStripePayFailurePostHog({
          stage: "flow_blocked",
          flow_surface: "mux_video_cta",
          failure_kind: "ar_pack_not_on_stripe",
          pack_id: packId,
        });
        console.error(`[createPaymentIntent] Pack "${packId}" not configured for Argentina`);
        return;
      }

      let stripeFailMeta: { http?: number; code?: string; kind?: string } = {};
      let amountCentsForTrack: number | undefined;

      try {
        // Para overridePackId (sorpresa, exclusive) priorizar static para que siempre exista precio y se genere el payment intent
        const packPricing =
          (overridePackId
            ? getPackPricingStatic(packId, effectiveCountry)
            : null) ??
          getPackPricing(packId, effectiveCountry) ??
          getPackPricingStatic(packId, effectiveCountry);
        if (!packPricing) {
          stripeFailMeta = { kind: "invalid_pack_id" };
          throw new Error("Invalid pack ID");
        }
        amountCentsForTrack = packPricing.amount;
        const apiUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}`
            : CREATE_PAYMENT_INTENT_ENDPOINT;
        const datafastMeta = getDataFastStripeMetadata();
        trackDataFastGoal("initiate_checkout", {
          flow: "mux_video_cta",
          pack_id: packId,
          currency: "usd",
          amount_cents: String(packPricing.amount),
          gateway: "stripe_payment_intent",
          country: (effectiveCountry || "US").toUpperCase().slice(0, 2),
        });
        // Custom Stripe: always USD regardless of country; amount is in USD cents
        const { ok: piOk, status: piStatus, data } = await fetchCheckoutJson<{
          clientSecret?: string;
          error?: string;
          message?: string;
          code?: string;
        }>(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: packPricing.amount,
            currency: "usd",
            packId: packId,
            buyerEmail: email || undefined, // Optional - Stripe will collect it in billing details
            source: window.location.pathname,
            country: effectiveCountry || undefined, // Fallback when server headers (IP country) are missing
            watcherLuckDiscountPercent: stripeWatcherLuckEligible
              ? stripeWatcherLuckPercent
              : 0,
            ...datafastMeta,
            metadata: {
              ...datafastMeta,
            },
          }),
        });

        if (!piOk) {
          let errMsg = data.error || data.message || "Failed to create payment intent";
          if (piStatus === 404) {
            errMsg =
              "Payment API not found (404). Check deployment and /api/create-payment-intent.";
          } else if (piStatus >= 500 && !data.error && !data.message) {
            errMsg = `Server error (${piStatus}). Check Railway logs for STRIPE_SECRET_KEY and API errors.`;
          }
          stripeFailMeta = {
            http: piStatus,
            code: data.code,
            kind: "http_error",
          };
          console.error(
            "[MuxVideoPlayerWithCTA] create-payment-intent failed:",
            piStatus,
            errMsg,
          );
          throw new Error(errMsg);
        }

        if (!data?.clientSecret) {
          stripeFailMeta = { kind: "missing_client_secret" };
          throw new Error("Invalid API response: missing clientSecret");
        }
        setClientSecret(data.clientSecret);
        setCheckoutState("CHECKOUT");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to initialize checkout";

        // Track payment intent creation error (GA + PostHog)
        trackButtonClickGA(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        if (
          typeof (
            window as unknown as {
              posthogSafeCapture?: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture === "function"
        ) {
          (
            window as unknown as {
              posthogSafeCapture: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture("payment_intent_error", {
            service,
            country: effectiveCountry,
            pathname: window.location.pathname,
            error_message: message.slice(0, 200),
            pack_id: packId,
          });
        }

        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "mux_video_cta",
          http_status: stripeFailMeta.http,
          stripe_error_code: stripeFailMeta.code,
          failure_kind: stripeFailMeta.kind,
          error_message: message,
          pack_id: packId,
          amount_cents: amountCentsForTrack,
          currency: "usd",
        });

        setCheckoutError(message);
        setCheckoutState("ERROR");
      } finally {
        setIsCreatingPaymentIntent(false);
      }
    },
    [
      effectiveCountry,
      getPackPricing,
      overridePackId,
      service,
      stripeWatcherLuckEligible,
      stripeWatcherLuckPercent,
    ],
  );

  const activateCtaFromUser = useCallback(() => {
    try {
      // Parent-controlled flows (e.g. BioTemplatePremium) must always win.
      if (onCTAClick) {
        if (parentCtaLockRef.current) {
          return;
        }
        parentCtaLockRef.current = true;
        if (parentCtaUnlockTimerRef.current !== null) {
          clearTimeout(parentCtaUnlockTimerRef.current);
        }
        parentCtaUnlockTimerRef.current = window.setTimeout(() => {
          parentCtaLockRef.current = false;
          parentCtaUnlockTimerRef.current = null;
        }, 900);
        Promise.resolve(onCTAClick(ctaUrl || "")).catch((callbackError) => {
          console.error(
            "[MuxVideoPlayerWithCTA] onCTAClick callback failed:",
            callbackError,
          );
        });
        return;
      }
      const isCheckoutEnabled = Boolean(STRIPE_PUBLISHABLE_KEY);
      const isPurchasableService =
        service === "channel_invite" || Boolean(overridePackId);
      if (!ctaUrlValid && !(isCheckoutEnabled && isPurchasableService)) {
        console.warn(
          "[MuxVideoPlayerWithCTA] CTA URL is not valid and custom checkout not available",
        );
        return;
      }

      console.log("[MuxVideoPlayerWithCTA] CTA activation:", {
        service,
        isCheckoutEnabled,
        isPurchasableService,
        isMobile,
        willUseCustomCheckout:
          isCheckoutEnabled && isPurchasableService && !isMobile,
        willRedirectToCheckoutPage:
          isCheckoutEnabled && isPurchasableService && isMobile,
      });

      if (isCheckoutEnabled && isPurchasableService) {
        // Intl VIP (Exclusive Membership): Stripe Checkout con contrato
        // prod/price/promo — email en Stripe → recovery vía capture-stripe-emails cron.
        if (
          service === "channel_invite" &&
          !overridePackId &&
          effectiveCountry !== "AR"
        ) {
          const languageForPlans =
            detectedLanguage === "es" ? "es-AR" : "en-US";
          trackButtonClickGA(
            "Exclusive membership Stripe checkout",
            "mux_intl_vip_stripe_checkout",
            "stripe_subscription",
            service,
            effectiveCountry,
            "MuxVideoPlayerWithCTA",
          );
          trackButtonClickPostHog(
            "Exclusive membership Stripe checkout",
            "mux_intl_vip_stripe_checkout",
            "stripe_subscription",
            service,
            effectiveCountry,
            "MuxVideoPlayerWithCTA",
          );
          const knownEmail = resolvePreCheckoutEmail(
            isSignedIn
              ? clerkUser?.primaryEmailAddress?.emailAddress
              : null,
          );
          const startIntlVip = (buyerEmail?: string | null) => {
            const dualVariant = getServerTemplateConfig()?.dualVariant;
            flushSync(() => setIsNavigatingAway(true));
            void launchIntlExclusiveMembershipCheckout({
              countryCode: effectiveCountry,
              pathname: window.location.pathname,
              languageForPlans,
              workerTemplate: slackFunnelTemplate ?? "MuxVideoPlayerWithCTA",
              email: buyerEmail?.trim() || undefined,
              variant: dualVariant
                ? {
                    id: dualVariant.id,
                    stripePriceId: dualVariant.stripePriceId,
                    priceUsd: dualVariant.priceUsd,
                    assignmentScope: dualVariant.assignmentScope,
                    assignmentGeo: dualVariant.assignmentGeo,
                    mpVariantLabel: dualVariant.mpVariantLabel,
                  }
                : null,
            }).catch((err) => {
              console.error(
                "[MuxVideoPlayerWithCTA] intl VIP checkout failed:",
                err,
              );
              setCheckoutError(
                err instanceof Error ? err.message : "Checkout unavailable",
              );
              setCheckoutState("ERROR");
              setIsNavigatingAway(false);
            });
          };
          if (knownEmail) {
            startIntlVip(knownEmail);
            return;
          }
          pendingIntlVipRef.current = {
            effectiveCountry,
            languageForPlans,
          };
          setPreCheckoutEmailModalOpen(true);
          return;
        }

        if (service === "channel_invite" && !overridePackId) {
          console.log(
            "[MuxVideoPlayerWithCTA] channel_invite flow - redirecting to smart router",
          );
          const routerUrl = `/checkout-channel-invite-router?pack=${effectivePackId}&country=${effectiveCountry || "US"}&lang=${detectedLanguage}`;
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = routerUrl;
          return;
        }

        const buttonLabel = isMobile
          ? "Custom checkout start (mobile)"
          : "Custom checkout start (desktop)";
        trackButtonClickGA(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        if (
          typeof (
            window as unknown as {
              posthogSafeCapture?: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture === "function"
        ) {
          (
            window as unknown as {
              posthogSafeCapture: (
                e: string,
                p?: Record<string, unknown>,
              ) => void;
            }
          ).posthogSafeCapture("custom_checkout_started", {
            service,
            country: effectiveCountry,
            pathname: window.location.pathname,
            is_mobile: isMobile,
          });
        }

        if (isMobile) {
          console.log(
            "[MuxVideoPlayerWithCTA] Mobile - redirecting to checkout page",
          );
          const checkoutUrl = `/checkout-channel-invite?pack=${effectivePackId}&country=${effectiveCountry || "US"}&lang=${detectedLanguage}`;
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = checkoutUrl;
          return;
        }

        console.log(
          "[MuxVideoPlayerWithCTA] Desktop - opening integrated checkout",
        );
        flushSync(() => setIsCreatingPaymentIntent(true));
        void createPaymentIntent(effectivePackId);
        return;
      }

      console.log(
        "[MuxVideoPlayerWithCTA] Falling back to URL navigation:",
        ctaUrl,
      );

      if (isInstagram) {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = ctaUrl;
        return;
      }
      if (openInNewTab) {
        const newWindow = window.open(
          ctaUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (!newWindow) {
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = ctaUrl;
        }
      } else {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = ctaUrl;
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in activateCtaFromUser:", error);
      try {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = ctaUrl;
      } catch (fallbackError) {
        console.error(
          "[MuxVideoPlayerWithCTA] Fallback navigation also failed:",
          fallbackError,
        );
        setIsNavigatingAway(false);
      }
    }
  }, [
    ctaUrl,
    ctaUrlValid,
    openInNewTab,
    onCTAClick,
    isInstagram,
    service,
    createPaymentIntent,
    isMobile,
    effectiveCountry,
    detectedLanguage,
    effectivePackId,
    overridePackId,
    parentCtaLockRef,
    parentCtaUnlockTimerRef,
    isSignedIn,
    clerkUser,
    slackFunnelTemplate,
  ]);

  const handleCTAClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Checkout handlers - email is now collected by Stripe in billing details

  const handleCheckoutSuccess = useCallback(
    (paymentIntentId?: string, paymentIntentSecret?: string) => {
      // Track custom checkout success before redirect (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      if (
        typeof (
          window as unknown as {
            posthogSafeCapture?: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture === "function"
      ) {
        (
          window as unknown as {
            posthogSafeCapture: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture("custom_checkout_success", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          payment_intent_id: paymentIntentId ?? undefined,
        });
      }

      // Determine language for thank you page
      const browserLang = navigator.language.toLowerCase();
      const isSpanish =
        browserLang.startsWith("es") || browserLang.includes("es-");
      const thankYouPath = isSpanish ? "/gracias" : "/thank-you";

      // Build redirect URL with payment_intent parameters (same format as Stripe redirect)
      const params = new URLSearchParams();
      if (paymentIntentId) {
        params.append("payment_intent", paymentIntentId);
      }
      if (paymentIntentSecret) {
        params.append("payment_intent_client_secret", paymentIntentSecret);
      }
      // Add redirect_status=succeeded to match Stripe's redirect format
      params.append("redirect_status", "succeeded");

      const redirectUrl = `${thankYouPath}?${params.toString()}`;

      // Redirect to thank you page
      flushSync(() => setIsNavigatingAway(true));
      window.location.href = redirectUrl;
    },
    [service, effectiveCountry],
  );

  const handleCheckoutError = useCallback(
    (error: string) => {
      // Track checkout error (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      if (
        typeof (
          window as unknown as {
            posthogSafeCapture?: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture === "function"
      ) {
        (
          window as unknown as {
            posthogSafeCapture: (
              e: string,
              p?: Record<string, unknown>,
            ) => void;
          }
        ).posthogSafeCapture("custom_checkout_error", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          error_message: error.slice(0, 200),
        });
      }

      trackStripePayFailurePostHog({
        stage: "payment_confirm",
        flow_surface: "mux_video_cta",
        failure_kind: "stripe_checkout_form",
        error_message: error,
        pack_id: effectivePackId,
        currency: "usd",
      });

      // Log error to console for debugging
      console.error("[MuxVideoPlayerWithCTA] Checkout error:", error);

      setCheckoutError(error);
      setCheckoutState("ERROR");
      setIsNavigatingAway(false);
    },
    [service, effectiveCountry, effectivePackId],
  );

  const handleBackToVideo = useCallback(() => {
    muxStripeJsUnavailableTrackedRef.current = false;
    setCheckoutState("VIDEO_PREVIEW");
    setClientSecret(null);
    setCheckoutError(null);
    setIsNavigatingAway(false);
  }, []);

  const handleRetryCheckout = useCallback(() => {
    setCheckoutError(null);
    setCheckoutState("VIDEO_PREVIEW");
    setIsNavigatingAway(false);
  }, []);

  // Open URL in Safari from iOS WebView (Instagram/Facebook in-app browser)
  // Uses Safari URL schemes - works to break out of embedded WebView
  const openInSafariFromWebView = useCallback((url: string) => {
    const xSafariScheme = `x-safari-https://${url.replace(/^https?:\/\//, "")}`;
    const legacyScheme = `com-apple-mobilesafari-tab:${url}`;
    setTimeout(() => {
      window.location.href = xSafariScheme;
    }, 50);
    setTimeout(() => {
      window.location.href = legacyScheme;
    }, 400);
    setTimeout(() => {
      window.location.href = url;
    }, 1200);
  }, []);

  // Handle Instagram banner open in browser action
  const handleOpenInBrowser = useCallback(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (!url) return;

    // 1. Copy to clipboard (fallback if Safari scheme fails, or for Android)
    const copyToClipboard = () => {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(url);
      }
      return Promise.reject(new Error("Clipboard not available"));
    };
    copyToClipboard()
      .then(() => {
        setLinkCopiedForBrowser(true);
        setTimeout(() => setLinkCopiedForBrowser(false), 4000);
      })
      .catch(() => {
        /* ignore */
      });

    trackButtonClickGA(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );

    // 2. iOS: Safari URL scheme - breaks out of Instagram WebView
    if (isIOS) {
      openInSafariFromWebView(url);
      return;
    }

    // 3. Android / other: share sheet (may offer "Open in Chrome")
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(() => {});
    }
  }, [service, effectiveCountry, isIOS, openInSafariFromWebView]);

  const handleDismissInstagramBanner = useCallback(() => {
    setShowInstagramBanner(false);
    setInstagramBannerDismissed(true);

    // Track banner dismissal
    trackButtonClickGA(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
  }, [service, effectiveCountry]);

  // Safari-specific touch handler for button
  const handleCTATouchStart = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // Prevent container from handling this touch
      touchStartRef.current = null;
    },
    [],
  );

  const handleCTATouchEnd = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Container styling — reel mobile/desktop alineado con MuxVideoPlayer
  // When Instagram Explorer: always add instagram-browser-container for viewport/scroll/video fixes
  const getContainerClasses = () => {
    const baseClasses = "relative bg-black group";
    const instagramClasses = isInstagram ? "instagram-browser-container" : "";

    if (isModal) {
      return `${baseClasses} w-full h-full min-h-0 flex items-center justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (isReelMode) {
      if (isMobile) {
        // Mobile: Fixed height, no scroll, fully contained
        return `${baseClasses} w-full h-dvh max-h-dvh overflow-hidden ${instagramClasses} ${containerClassName}`;
      }
      // Desktop: Use h-full to respect parent container, no min-height to avoid scroll
      return `${baseClasses} w-full h-full flex items-start justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "contain") {
      if (fillsParent) {
        return `${baseClasses} mux-viewport-container w-full h-full min-h-0 max-h-none flex items-center justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
      }
      return `${baseClasses} w-full h-full min-h-[400px] max-h-[90vh] flex items-center justify-center ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "cover") {
      return `${baseClasses} w-full h-full min-h-[80vh] max-h-[90vh] ${instagramClasses} ${containerClassName}`;
    }
    return `${baseClasses} w-full h-full min-h-[400px] max-h-[800px] ${instagramClasses} ${containerClassName}`;
  };

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isReelMode || isMobile) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(".action-button-container") ||
        target.closest(".mux-cta-close-btn")
      )
        return;
      // If video is playing: click to toggle mute (same as mobile tap behavior)
      // If video is paused: click to toggle play/pause
      if (isPlaying) {
        toggleMute();
      } else {
        togglePlayPause();
      }
    },
    [isReelMode, isMobile, togglePlayPause, toggleMute, isPlaying],
  );

  const getVideoStyles = useCallback(() => {
    const id = safeCssId;
    if (isReelMode) {
      if (isMobile) {
        // Platform-specific height handling
        const heightStyles = isIOS
          ? `
            /* iOS Safari specific - use multiple fallbacks for dynamic toolbar */
            height: 100vh !important;
            height: 100dvh !important;
            height: -webkit-fill-available !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            min-height: -webkit-fill-available !important;
          `
          : `
            /* Android Chrome - dvh works well */
            height: 100dvh !important;
            height: 100vh !important;
            min-height: 100dvh !important;
            min-height: 100vh !important;
          `;

        // Landscape mode adjustments
        const landscapeStyles = isLandscape
          ? `
          /* Landscape orientation adjustments using Mux CSS variables */
          @media (orientation: landscape) {
            .mux-video-player-cta-${id} {
              height: 100dvh !important;
              height: 100vh !important;
            }
            .mux-video-player-cta-${id} mux-player {
              --media-object-fit: contain;
              --media-object-position: center;
            }
          }
        `
          : "";

        return `
          .mux-video-player-cta-${id} {
            width: 100vw !important;
            ${heightStyles}
            max-height: 100dvh !important;
            max-height: 100vh !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            overflow: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            /* GPU acceleration for smoother scrolling */
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            will-change: transform !important;
            /* TikTok style: full immersive screen */
            margin: 0 !important;
            padding: 0 !important;
          }
          .mux-video-player-cta-${id} mux-player {
            width: 100% !important;
            height: 100% !important;
            min-height: 100dvh !important;
            min-height: 100vh !important;
            max-height: 100dvh !important;
            max-height: 100vh !important;
            /* Use official Mux CSS variables for video sizing - TikTok style cover */
            --media-object-fit: cover;
            --media-object-position: center;
          }
          /* Never show native Mux bottom control strip (product rule: CTA only) */
          .mux-video-player-cta-${id} mux-player::part(bottom),
          .mux-video-player-cta-${id} mux-player::part(control-bar) {
            display: none !important;
            opacity: 0 !important;
            pointer-events: none !important;
            visibility: hidden !important;
            height: 0 !important;
            max-height: 0 !important;
          }
          /* Video element inherits from --media-object-fit and --media-object-position */
          .mux-video-player-cta-${id} mux-player video {
            width: 100% !important;
            height: 100% !important;
            /* Ensure inline playback on all mobile devices */
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          /* iOS Safari specific viewport fixes */
          @supports (-webkit-touch-callout: none) {
            .mux-video-player-cta-${id} {
              height: -webkit-fill-available !important;
              min-height: -webkit-fill-available !important;
            }
          }
          /* Android specific optimizations */
          ${
            isAndroid
              ? `
          .mux-video-player-cta-${id} {
            /* Android Chrome handles dvh well */
            height: 100dvh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* Ensure inline playback on Android */
            playsinline: true !important;
          }
          `
              : ""
          }
          /* iOS specific optimizations */
          ${
            isIOS
              ? `
          .mux-video-player-cta-${id} {
            /* iOS needs -webkit-fill-available for proper height */
            height: -webkit-fill-available !important;
            /* Prevent iOS rubber-banding effect */
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* iOS requires webkit prefix */
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          `
              : ""
          }
          ${landscapeStyles}
          ${
            isInstagram
              ? `
          .mux-video-player-cta-${id} {
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-y pinch-zoom !important;
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            min-height: 100svh !important;
            height: 100svh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} button,
          .mux-video-player-cta-${id} .action-button-container button {
            -webkit-tap-highlight-color: transparent !important;
            touch-action: manipulation !important;
            min-height: 48px !important;
            min-width: 48px !important;
            -webkit-user-select: none !important;
            user-select: none !important;
          }
          `
              : ""
          }
        `;
      }
      return `
        .mux-video-player-cta-${id} {
          width: 100% !important;
          height: 100% !important;
          min-height: 100vh !important;
          display: flex !important;
          align-items: stretch !important;
          justify-content: center !important;
          background-color: #000 !important;
          position: relative !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id}[data-mux-reel-viewport="page"] {
          height: 100dvh !important;
          height: 100vh !important;
          min-height: 100dvh !important;
          min-height: 100vh !important;
        }
        .mux-video-player-cta-${id} mux-player {
          width: auto !important;
          height: 100% !important;
          max-width: min(56.25vh, 100%) !important;
          max-height: 100dvh !important;
          max-height: 100vh !important;
          min-width: 320px !important;
          margin: 0 auto !important;
          display: block !important;
          position: relative !important;
          /* Use official Mux CSS variables for video sizing */
          --media-object-fit: contain;
          --media-object-position: center;
        }
        /* Never show native Mux bottom control strip (product rule: CTA only) */
        .mux-video-player-cta-${id} mux-player::part(bottom),
        .mux-video-player-cta-${id} mux-player::part(control-bar) {
          display: none !important;
          opacity: 0 !important;
          pointer-events: none !important;
          visibility: hidden !important;
          height: 0 !important;
          max-height: 0 !important;
        }
        /* Video element inherits from --media-object-fit and --media-object-position */
        .mux-video-player-cta-${id} mux-player video {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 85vw) !important;
          }
        }
        @media (min-width: 1025px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 90vw) !important;
          }
        }
        @media (min-width: 1920px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 1200px) !important;
          }
        }
      `;
    }
    if (currentDisplayMode === "cover") {
      return `
        .mux-video-player-cta-${id} mux-player {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }
    const objectFit = fillsParent && isMobile ? "cover" : "contain";
    const minHeightUnit = fillsParent ? "0" : "100vh";
    return `
      .mux-video-player-cta-${id} {
        width: 100% !important;
        height: 100% !important;
        min-height: ${minHeightUnit} !important;
        max-height: 100% !important;
        display: flex !important;
        align-items: stretch !important;
        justify-content: center !important;
        overflow: hidden !important;
      }
      .mux-video-player-cta-${id} mux-player {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        min-width: 0 !important;
        --media-object-fit: ${objectFit};
        --media-object-position: center center;
      }
      .mux-video-player-cta-${id} mux-player video {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: ${objectFit} !important;
        object-position: center center !important;
      }
    `;
  }, [
    isReelMode,
    isMobile,
    isInstagram,
    isIOS,
    isAndroid,
    isLandscape,
    currentDisplayMode,
    fillsParent,
    safeCssId,
  ]);

  // Get CTA button styles to ensure visibility and proper positioning
  const getCTAButtonStyles = useCallback(() => {
    const id = safeCssId;
    const isValid = Boolean(showCTAButton);
    
    // Detectar si Special Offer está activo para aplicar estilo verde (más conversión)
    const specialOfferEnv = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER as string)?.toLowerCase().trim();
    const isSpecialOfferActive = specialOfferEnv === "true" || specialOfferEnv === "1" || specialOfferEnv === "active";

    // Base button styles (shared across all modes)
    const baseButtonStyles = `
      /* Instagram browser banner animation */
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-slide-down {
        animation: slideDown 0.3s ease-out;
      }

      .mux-video-player-cta-${id} .cta-action-button {
        z-index: 51 !important;
        position: relative !important;
        pointer-events: auto !important;
        visibility: visible !important;
        opacity: ${isValid ? 1 : 0.7} !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: ${isSpecialOfferActive 
          ? "linear-gradient(to right, #10b981, #059669, #047857) !important" 
          : "linear-gradient(to right, #a855f7, #9333ea, #ec4899) !important"};
        color: white !important;
        font-weight: 700 !important;
        letter-spacing: 0.01em !important;
        border-radius: 9999px !important;
        box-shadow: ${isSpecialOfferActive
          ? "0 4px 14px 0 rgba(16, 185, 129, 0.5), 0 2px 4px 0 rgba(5, 150, 105, 0.4) !important"
          : "0 4px 14px 0 rgba(139, 92, 246, 0.4), 0 2px 4px 0 rgba(236, 72, 153, 0.3) !important"};
        transition: all 0.3s ease-out !important;
        cursor: pointer !important;
        -webkit-tap-highlight-color: transparent !important;
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
        touch-action: manipulation !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) {
        background: ${isSpecialOfferActive
          ? "linear-gradient(to right, #059669, #047857, #065f46) !important"
          : "linear-gradient(to right, #9333ea, #7e22ce, #db2777) !important"};
        box-shadow: ${isSpecialOfferActive
          ? "0 6px 20px 0 rgba(16, 185, 129, 0.6), 0 4px 8px 0 rgba(5, 150, 105, 0.5) !important"
          : "0 6px 20px 0 rgba(139, 92, 246, 0.5), 0 4px 8px 0 rgba(236, 72, 153, 0.4) !important"};
        border-color: rgba(255, 255, 255, 0.2) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:active:not(:disabled) {
        transform: scale(0.97) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:disabled {
        background: linear-gradient(to right, #4b5563, #374151) !important;
        cursor: not-allowed !important;
        opacity: 0.7 !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        pointer-events: none !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        flex-shrink: 1 !important;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
        letter-spacing: 0.025em !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)) !important;
        transition: transform 0.3s ease-out !important;
      }
      .mux-video-player-cta-${id} .cta-action-button svg[class*="lucide-instagram"] {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) .cta-button-icon {
        transform: translate(2px, -2px) !important;
      }
    `;

    if (isReelMode && !isMobile) {
      // Desktop/tablet reel: button higher so it stays visible when close (X) or fullscreen layout compresses the view
      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 100 !important;
          position: absolute !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          /* Match mobile reel: sit on the video column bottom (column is full viewport height) */
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: 50% !important;
          right: auto !important;
          width: min(56.25vh, 90vw) !important;
          max-width: min(56.25vh, 90vw) !important;
          transform: translateX(-50%) !important;
          padding: 0 1rem !important;
          box-sizing: border-box !important;
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .mux-video-player-cta-${id} .action-button-container {
            width: min(56.25vh, 85vw) !important;
            max-width: min(56.25vh, 85vw) !important;
          }
        }
        @media (min-width: 1920px) {
          .mux-video-player-cta-${id} .action-button-container {
            width: min(56.25vh, 1200px) !important;
            max-width: min(56.25vh, 1200px) !important;
          }
        }
        .mux-video-player-cta-${id} .cta-action-button {
          min-height: 56px !important;
          height: 56px !important;
          padding: 0 1.5rem !important;
          font-size: 1rem !important;
          gap: 0.625rem !important;
          width: 100% !important;
          flex-shrink: 0 !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 1rem !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.25rem !important;
          height: 1.25rem !important;
        }
      `
      );
    }

    if (isMobile) {
      // Mobile: centered at bottom with safe-area insets (iPhone notch, home indicator)
      // Platform-specific optimizations for iOS and Android
      const platformStyles = isIOS
        ? `
        /* iOS specific - ensure proper touch handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-appearance: none !important;
          -webkit-tap-highlight-color: transparent !important;
          /* Prevent iOS text selection on long press */
          -webkit-user-select: none !important;
          -webkit-touch-callout: none !important;
        }
      `
        : isAndroid
          ? `
        /* Android specific - better ripple effect handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1) !important;
        }
      `
          : "";

      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 150 !important;
          position: fixed !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          /* TikTok Shop style - bottom with safe-area for all devices */
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: max(1rem, env(safe-area-inset-left, 1rem)) !important;
          right: max(1rem, env(safe-area-inset-right, 1rem)) !important;
          width: auto !important;
          min-width: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id} .cta-action-button {
          /* TikTok Shop style - prominent button */
          min-height: 56px !important;
          height: 56px !important;
          flex-shrink: 0 !important;
          padding: 0 1.75rem !important;
          font-size: 1.0625rem !important;
          font-weight: 800 !important;
          gap: 0.75rem !important;
          flex: 1 !important;
          max-width: 100% !important;
          width: 100% !important;
          /* Prevent double-tap zoom on button */
          touch-action: manipulation !important;
          /* Enhanced shadow for TikTok Shop look */
          box-shadow: 0 8px 24px 0 rgba(139, 92, 246, 0.6), 0 4px 12px 0 rgba(236, 72, 153, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.15) !important;
          /* Backdrop blur for better visibility on any video */
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 1rem !important;
          font-weight: 800 !important;
          letter-spacing: 0.02em !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.25rem !important;
          height: 1.25rem !important;
        }
        /* Small mobile devices (iPhone SE, small Android) */
        @media (max-width: 375px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(1rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem)) !important;
            left: max(0.75rem, env(safe-area-inset-left, 0.75rem)) !important;
            right: max(0.75rem, env(safe-area-inset-right, 0.75rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            gap: 0.625rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-icon {
            width: 1.125rem !important;
            height: 1.125rem !important;
          }
        }
        /* Extra small devices (very small Android phones) */
        @media (max-width: 320px) {
          .mux-video-player-cta-${id} .action-button-container {
            padding: 0 !important;
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(0.75rem, env(safe-area-inset-left)) !important;
            right: max(0.75rem, env(safe-area-inset-right)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
        }
        /* Medium mobile devices (iPhone 12/13/14) */
        @media (min-width: 376px) and (max-width: 414px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
        }
        /* Large mobile devices (iPhone Pro Max, large Android) */
        @media (min-width: 415px) and (max-width: 767px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 58px !important;
            height: 58px !important;
            padding: 0 1.75rem !important;
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
        }
        /* Landscape orientation - adjust for side notches, keep button visible */
        @media (orientation: landscape) and (max-height: 500px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(2rem, calc(env(safe-area-inset-left) + 0.5rem)) !important;
            right: max(2rem, calc(env(safe-area-inset-right) + 0.5rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 48px !important;
            height: 48px !important;
            padding: 0 1.25rem !important;
            font-size: 0.875rem !important;
          }
        }
        /* Instagram / Meta in-app browser: más margen inferior (chrome del WebView) */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(1.75rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
          }
        `
            : ""
        }
        ${platformStyles}
      `
      );
    }

    // Desktop non-reel mode: full width with padding, button at bottom
    return (
      baseButtonStyles +
      `
      .mux-video-player-cta-${id} .action-button-container {
        z-index: 100 !important;
        position: absolute !important;
        pointer-events: auto !important;
        visibility: visible !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        bottom: max(2rem, env(safe-area-inset-bottom)) !important;
        left: 1.5rem !important;
        right: 1.5rem !important;
        width: auto !important;
        max-width: calc(100% - 3rem) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button {
        min-height: 56px !important;
        height: 56px !important;
        padding: 0 1.5rem !important;
        font-size: 1rem !important;
        gap: 0.625rem !important;
        max-width: 32rem !important;
        width: 100% !important;
        flex-shrink: 0 !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        font-size: 1rem !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        width: 1.25rem !important;
        height: 1.25rem !important;
      }
    `
    );
  }, [
    safeCssId,
    isMobile,
    isReelMode,
    isInstagram,
    isIOS,
    isAndroid,
    showCTAButton,
  ]);

  // Function to retry loading the video
  const handleRetry = useCallback(() => {
    setHasError(false);
    setErrorMessage("");
    setIsLoading(true);
    setHasReceivedMuxMetadata(false);
    setMuxHasLoadedData(false);
    setMuxCanPlayBuffered(false);
    setHasPlaybackStarted(false);
    setShowPlayButton(false);
    setLoadingSeconds(0);
    setRetryCount(0);

    // Force reload the player
    const player = playerRef.current;
    if (player) {
      if (typeof player.load === "function") {
        player.load();
      } else if (typeof player.play === "function") {
        // Fallback: try to play
        player.play().catch((error: unknown) => {
          console.debug("[MuxVideoPlayerWithCTA] Retry play failed:", error);
        });
      }
    }
  }, []);

  // Optimized thumbnail + LQIP via shared Mux presentation helper (CDN buckets).
  const thumbnailFitMode = isReelMode && isMobile ? "smartcrop" : "preserve";
  const thumbnailObjectFit = isReelMode && isMobile ? "cover" : "contain";

  const thumbnailUrl = buildMuxThumbnailUrl(effectivePlaybackId, {
    time: 0,
    width: muxPosterWidthBucket({ mobile: isMobile }),
    quality: isMobile ? 75 : 80,
    fitMode: thumbnailFitMode,
  });

  const placeholderUrl = buildMuxLqipUrl(effectivePlaybackId, {
    time: 0,
    fitMode: thumbnailFitMode,
  });

  const renderMuxLoadChrome = () => (
    <>
      {showHeavyVideoLoader && (
        <div
          className={`absolute inset-0 z-40 flex items-center justify-center transition-colors duration-500 ${
            muxHasLoadedData
              ? "bg-black/25 backdrop-blur-[1px]"
              : "bg-black/42 backdrop-blur-[2px]"
          }`}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("loadingVideo", detectedLanguage)}
          style={{
            paddingTop: "max(1rem, env(safe-area-inset-top, 0px))",
            paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
            paddingLeft: "max(1rem, env(safe-area-inset-left, 0px))",
            paddingRight: "max(1rem, env(safe-area-inset-right, 0px))",
          }}
        >
          <div className="text-center space-y-4 px-6 py-4 max-w-md mx-auto">
            <div className="flex justify-center gap-2" aria-hidden="true">
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  hasReceivedMuxMetadata
                    ? "bg-fuchsia-500"
                    : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxHasLoadedData ? "bg-fuchsia-500" : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxCanPlayBuffered
                    ? "bg-fuchsia-400"
                    : hasReceivedMuxMetadata && muxHasLoadedData
                      ? "bg-white/40 animate-pulse"
                      : "bg-white/15"
                }`}
              />
            </div>
            <div className="relative mx-auto w-16 h-16 md:w-20 md:h-20">
              <div
                className="absolute inset-0 w-full h-full border-4 border-white/15 border-t-amber-400 rounded-full animate-spin"
                style={{
                  animationDuration: "1s",
                  willChange: "transform",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-6 h-6 md:w-8 md:h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div
                className="absolute inset-0 w-full h-full bg-amber-400 rounded-full blur-lg opacity-15"
                style={{ willChange: "opacity" }}
              />
            </div>

            <div className="space-y-1">
              <p className="text-white text-sm md:text-base font-semibold tracking-wide">
                {!hasReceivedMuxMetadata
                  ? t("loadingVideo", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("loadingVideo", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
              </p>
              <p className="text-white/85 text-xs font-medium">
                {!hasReceivedMuxMetadata
                  ? t("connectingToPlayer", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("decodingPreview", detectedLanguage)
                    : detectedLanguage === "es"
                      ? `Tiempo de carga: ${loadingSeconds}s`
                      : `Loading time: ${loadingSeconds}s`}
              </p>
              {!isMobile && hasReceivedMuxMetadata && muxHasLoadedData && (
                <p className="text-gray-300 text-xs font-medium opacity-85">
                  {t("startingPlayback", detectedLanguage)}
                </p>
              )}
              {shouldShowSlowConnectionHint && (
                <p className="text-white/70 text-xs font-medium">
                  {isLikelySlowNetwork
                    ? t("slowConnection", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
                </p>
              )}
              {isInstagram && (
                <p className="text-white/70 text-xs leading-relaxed pt-1">
                  {t("inAppBrowserVideoLoadHint", detectedLanguage)}
                </p>
              )}
            </div>

            {isMobile && (hasReceivedMuxMetadata || muxHasLoadedData) && (
              <div className="flex items-center justify-center gap-1.5">
                <div
                  className="w-1.5 h-1.5 bg-amber-400/80 rounded-full animate-bounce"
                  style={{
                    animationDelay: "0ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-amber-400/80 rounded-full animate-bounce"
                  style={{
                    animationDelay: "150ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-amber-400/80 rounded-full animate-bounce"
                  style={{
                    animationDelay: "300ms",
                    animationDuration: "1s",
                  }}
                />
              </div>
            )}
            {loadingSeconds >= 10 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetry();
                }}
                className="mt-3 px-4 py-2 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              >
                {detectedLanguage === "es"
                  ? "Reintentar carga"
                  : "Retry loading"}
              </button>
            )}
          </div>
        </div>
      )}
      {showLightVideoLoader && (
        <div
          className={`absolute inset-x-0 z-40 flex justify-center px-4 pointer-events-none ${
            isInstagram ? "bottom-20 md:bottom-24" : "bottom-6 md:bottom-8"
          }`}
          style={{
            paddingBottom: isInstagram
              ? "max(2rem, calc(env(safe-area-inset-bottom, 0px) + 3.5rem))"
              : "max(0.5rem, env(safe-area-inset-bottom, 0px))",
          }}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("playbackReadyBrief", detectedLanguage)}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/82 px-4 py-2.5 shadow-lg backdrop-blur-md max-w-[min(100%,22rem)]">
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-fuchsia-300"
              aria-hidden
            />
            <span className="text-xs font-medium text-white/95 text-center">
              {t("playbackReadyBrief", detectedLanguage)}
            </span>
          </div>
        </div>
      )}
    </>
  );

  // No playbackId error - show configuration error (use effectivePlaybackId so Spain fallback counts)
  if (!effectivePlaybackId || effectivePlaybackId.trim() === "") {
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <span className="text-yellow-400 text-2xl">⚙️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">
            Video Configuration Missing
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            No video playback ID configured for this service.
            {service && ` (Service: ${service})`}
            {countryCode && ` (Country: ${countryCode})`}
          </p>
          <p className="text-xs text-gray-400">
            Please check environment variables or service configuration.
          </p>
        </div>
      </div>
    );
  }

  // Error state - strict mode: block render for all services, including channel_invite.
  if (
    hasError &&
    errorMessage &&
    !errorMessage.toLowerCase().includes("network") &&
    !errorMessage.toLowerCase().includes("connection")
  ) {
    // Only block for invalid videos, not network errors
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
        style={
          thumbnailUrl && !thumbnailError
            ? {
                backgroundImage: `url(${thumbnailUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">⚠️</span>
          </div>
          {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
          <p className="text-sm text-gray-300 mb-4">{errorMessage}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setHasError(false);
                setErrorMessage("");
                onClose?.();
              }}
              className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700/70 text-white rounded-full text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>

        {/* Hidden image to preload thumbnail and detect errors */}
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            className="hidden"
            onError={() => {
              setThumbnailError(true);
            }}
            onLoad={() => {
              setThumbnailError(false);
            }}
            loading="lazy"
          />
        )}
      </div>
    );
  }

  // For network errors, continue rendering - show video player with poster and CTA
  // This ensures BioTemplate always provides a good UX even with VPN/network issues

  // Show loading state while service config is being loaded
  // Note: For services, apiVideoConfig always provides valid fallbacks, so we don't need to wait
  // This only blocks rendering when we truly have no playbackId available
  if (isLoadingConfig && !playbackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto" />
          <div className="text-white text-sm">
            Loading video configuration...
          </div>
        </div>
      </div>
    );
  }

  if (!effectivePlaybackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-white text-sm">
          {service
            ? `No playback ID configured for service: ${service}`
            : "No playback ID provided"}
        </div>
      </div>
    );
  }

  // Get pack info (effectivePackId: channel_invite or overridePackId e.g. exclusive); fallback to static so checkout always renders when we have clientSecret.
  // Para sorpresa/exclusive priorizar siempre static para que el custom element tenga precio y se renderice.
  const packPricing =
    (overridePackId
      ? getPackPricingStatic(effectivePackId, effectiveCountry)
      : null) ??
    getPackPricing(effectivePackId, effectiveCountry) ??
    getPackPricingStatic(effectivePackId, effectiveCountry);

  // Precio mostrado y enviado al PI: pack list + reglas exclusivo + bonus "suerte" (solo Stripe).
  const watcherCheckoutPricing = computeStripePackCheckoutAmounts({
    baseAmountCents: packPricing?.amount ?? 0,
    packId: effectivePackId,
    watcherLuckPercent: stripeWatcherLuckPercent,
  });

  const checkoutDisplayAmount =
    packPricing == null ? 0 : watcherCheckoutPricing.finalAmountCents;
  const checkoutOriginalAmount =
    watcherCheckoutPricing.showDiscountBadge
      ? watcherCheckoutPricing.originalListAmountCents
      : undefined;
  const checkoutDiscountPercent =
    watcherCheckoutPricing.discountPercentDisplay ?? undefined;

  // Format price for display
  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Email collection removed - Stripe collects email in billing details

  // Loading state while creating payment intent
  const checkoutContentWrapper = (content: React.ReactNode) => {
    const inner = (
      <div
        className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
        style={{ minHeight: 0, flex: 1 }}
      >
        {content}
      </div>
    );
    if (isModal) {
      return (
        <div
          role="dialog"
          aria-modal="true"
          className={`w-full h-full flex items-center justify-center p-4 bg-black/70 ${isInstagram ? "instagram-browser-modal" : ""}`}
        >
          <div className="w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col">
            {inner}
          </div>
        </div>
      );
    }
    return inner;
  };

  if (ctaBusy) {
    const loadingCopy = isNavigatingAway
      ? t("openingCheckoutPage", detectedLanguage)
      : t("preparingCheckout", detectedLanguage);
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex flex-col items-center justify-center bg-black gap-4 p-4"
        aria-busy="true"
        aria-live="polite"
        aria-label={loadingCopy}
      >
        <div
          className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-white/80 text-sm text-center max-w-xs px-2">
          {loadingCopy}
        </p>
      </div>,
    );
  }

  // Render checkout state: solo si tenemos clientSecret, packPricing y monto final válido (coincide con PaymentIntent)
  if (checkoutState === "CHECKOUT" && clientSecret && packPricing && checkoutDisplayAmount > 0) {
    const stripePromise = getStripe();
    if (!stripePromise) {
      if (!muxStripeJsUnavailableTrackedRef.current) {
        muxStripeJsUnavailableTrackedRef.current = true;
        trackStripePayFailurePostHog({
          stage: "stripe_js_unavailable",
          flow_surface: "mux_video_cta",
          failure_kind: "stripe_promise_null",
          pack_id: effectivePackId,
        });
      }
      return checkoutContentWrapper(
        <div className="w-full h-full flex items-center justify-center bg-black p-4">
          <div className="text-white text-center">
            <p className="text-red-400 mb-4">Checkout not configured</p>
            <button
              onClick={handleBackToVideo}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              Volver
            </button>
          </div>
        </div>,
      );
    }

    return checkoutContentWrapper(
      <div className="w-full h-full bg-black flex flex-col min-h-0">
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: STRIPE_APPEARANCE,
            locale: detectedLanguage === "es" ? "es" : "en",
            loader: 'auto',
          }}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <StripeCheckoutForm
              amount={checkoutDisplayAmount}
              currency="usd"
              packName={
                detectedLanguage === "es" ? packPricing.nameEs : packPricing.name
              }
              packThumbnail={packPricing.thumbnail}
              originalAmount={checkoutOriginalAmount}
              discountPercent={checkoutDiscountPercent}
              language={detectedLanguage === "es" ? "es" : "en"}
              onSuccess={handleCheckoutSuccess}
              onError={handleCheckoutError}
              onBack={handleBackToVideo}
              flowSurface="mux_video_stripe_checkout"
            />
          </div>
        </Elements>
      </div>,
    );
  }

  // Note: SUCCESS state redirects to /thank-you or /gracias, no need to render

  // Render invalid playbackId error (before attempting to load video)
  if (!isValidPlaybackId && !isLoadingConfig) {
    const errorTitle =
      detectedLanguage === "es" ? "Video no disponible" : "Video unavailable";
    const errorMsg =
      detectedLanguage === "es"
        ? "El video no está configurado correctamente. Por favor contacta a soporte."
        : "The video is not configured correctly. Please contact support.";
    const backLabel = detectedLanguage === "es" ? "Cerrar" : "Close";

    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-white/70"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">{errorMsg}</p>
            {import.meta.env.DEV && (
              <p className="text-white/50 text-xs mt-4 font-mono">
                Debug: playbackId="{effectivePlaybackId}" country=
                {effectiveCountry} service={service}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation"
            >
              {backLabel}
            </button>
          )}
        </div>
      </div>,
    );
  }

  // Render error state (same container as video for responsive)
  if (checkoutState === "ERROR") {
    const errorTitle = t("paymentError", detectedLanguage);
    const errorDefaultMsg = t("paymentErrorMsg", detectedLanguage);
    const retryLabel = t("retry", detectedLanguage);
    const backLabel = t("back", detectedLanguage);
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">
              {checkoutError || errorDefaultMsg}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleRetryCheckout}
              className="w-full bg-white text-black font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/90 active:bg-white/95 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {retryLabel}
            </button>
            <button
              type="button"
              onClick={handleBackToVideo}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>,
    );
  }

  // If content is inactive for this country (API soft delete), show message
  if (!isContentActive) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8 text-center">
        <div className="max-w-md">
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Contenido no disponible
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Este contenido no está disponible en tu región en este momento.
          </p>
        </div>
      </div>
    );
  }

  // Render video preview (default state)
  return (
    <>
      <CheckoutEmailCaptureModal
        open={preCheckoutEmailModalOpen}
        lang={detectedLanguage === "es" ? "es" : "en"}
        surface="MuxVideoPlayerWithCTA"
        template={slackFunnelTemplate ?? "MuxVideoPlayerWithCTA"}
        allowSkip
        initialEmail={
          resolvePreCheckoutEmail(
            isSignedIn
              ? clerkUser?.primaryEmailAddress?.emailAddress
              : null,
          ) || ""
        }
        onSubmit={(email) => {
          const pending = pendingIntlVipRef.current;
          setPreCheckoutEmailModalOpen(false);
          pendingIntlVipRef.current = null;
          if (!pending) return;
          const dualVariant = getServerTemplateConfig()?.dualVariant;
          flushSync(() => setIsNavigatingAway(true));
          void launchIntlExclusiveMembershipCheckout({
            countryCode: pending.effectiveCountry,
            pathname: window.location.pathname,
            languageForPlans: pending.languageForPlans,
            workerTemplate: slackFunnelTemplate ?? "MuxVideoPlayerWithCTA",
            email,
            variant: dualVariant
              ? {
                  id: dualVariant.id,
                  stripePriceId: dualVariant.stripePriceId,
                  priceUsd: dualVariant.priceUsd,
                  assignmentScope: dualVariant.assignmentScope,
                  assignmentGeo: dualVariant.assignmentGeo,
                  mpVariantLabel: dualVariant.mpVariantLabel,
                }
              : null,
          }).catch((err) => {
            console.error(
              "[MuxVideoPlayerWithCTA] intl VIP checkout failed:",
              err,
            );
            setCheckoutError(
              err instanceof Error ? err.message : "Checkout unavailable",
            );
            setCheckoutState("ERROR");
            setIsNavigatingAway(false);
          });
        }}
        onSkip={() => {
          const pending = pendingIntlVipRef.current;
          setPreCheckoutEmailModalOpen(false);
          pendingIntlVipRef.current = null;
          if (!pending) return;
          const dualVariant = getServerTemplateConfig()?.dualVariant;
          flushSync(() => setIsNavigatingAway(true));
          void launchIntlExclusiveMembershipCheckout({
            countryCode: pending.effectiveCountry,
            pathname: window.location.pathname,
            languageForPlans: pending.languageForPlans,
            workerTemplate: slackFunnelTemplate ?? "MuxVideoPlayerWithCTA",
            email: undefined,
            variant: dualVariant
              ? {
                  id: dualVariant.id,
                  stripePriceId: dualVariant.stripePriceId,
                  priceUsd: dualVariant.priceUsd,
                  assignmentScope: dualVariant.assignmentScope,
                  assignmentGeo: dualVariant.assignmentGeo,
                  mpVariantLabel: dualVariant.mpVariantLabel,
                }
              : null,
          }).catch((err) => {
            console.error(
              "[MuxVideoPlayerWithCTA] intl VIP checkout failed:",
              err,
            );
            setCheckoutError(
              err instanceof Error ? err.message : "Checkout unavailable",
            );
            setCheckoutState("ERROR");
            setIsNavigatingAway(false);
          });
        }}
        onClose={() => {
          setPreCheckoutEmailModalOpen(false);
          pendingIntlVipRef.current = null;
        }}
      />
      {/* Custom styles for video player */}
      <style>{getVideoStyles()}</style>
      {/* Custom styles for CTA button to ensure visibility and perfect responsiveness */}
      <style>{getCTAButtonStyles()}</style>
      {/* Mux Player custom styling using official CSS variables and parts */}
      <style>{`
        /* Use Mux official CSS variable to control media object fit */
        .mux-video-player-cta-${safeCssId} mux-player {
          --media-object-fit: ${isReelMode && isMobile ? "cover" : "contain"};
          --media-object-position: center;
        }
        
        /* Instagram Explorer optimizations */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${safeCssId} mux-player {
            /* Force native playback for better Instagram compatibility */
            -webkit-playsinline: true;
            playsinline: true;
            /* Prevent Instagram from hijacking video controls */
            pointer-events: auto;
          }
          .mux-video-player-cta-${safeCssId} mux-player video {
            /* Ensure video plays inline in Instagram without fullscreen */
            -webkit-playsinline: true;
            playsinline: true;
            /* Prevent Instagram scroll interference */
            touch-action: none;
          }
        `
            : ""
        }
        
        /* TikTok Shop style: Hide ALL native controls (no bottom bar on any surface) */
        ${muxHideNativeControlsCss(`.mux-video-player-cta-${safeCssId} mux-player`)}
        
        /* Floating CTA (buy) must sit above video layers */
        .mux-video-player-cta-${safeCssId} .action-button-container {
          z-index: 100 !important;
        }

        /* Full-frame loading ambience (conversion: no "empty black" flashes) */
        @keyframes muxLoadShimmer-${safeCssId} {
          0% {
            transform: translateX(-55%);
            opacity: 0.2;
          }
          45% {
            opacity: 0.55;
          }
          100% {
            transform: translateX(55%);
            opacity: 0.2;
          }
        }
        .mux-video-player-cta-${safeCssId} .mux-load-shimmer-${safeCssId} {
          position: absolute;
          inset: 0;
          width: 220%;
          left: -60%;
          background: linear-gradient(
            104deg,
            transparent 42%,
            rgba(236, 72, 153, 0.12) 50%,
            rgba(167, 139, 250, 0.2) 52%,
            transparent 62%
          );
          animation: muxLoadShimmer-${safeCssId} 3s ease-in-out infinite;
          mix-blend-mode: screen;
          pointer-events: none;
        }
      `}</style>
      {isModal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || "Video"}
          className={`w-full h-full flex items-center justify-center p-4 bg-black/70 transition-opacity duration-200 ${
            modalMounted ? "opacity-100" : "opacity-0"
          } ${isInstagram ? "instagram-browser-modal" : ""}`}
          onClick={handleBackdropClick}
        >
          <div
            className={`w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col transition-all duration-200 ${
              modalMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onClick={handleContainerClick}
              role="presentation"
              aria-busy={awaitingFirstPlayback}
              style={
                {
                  backgroundColor: "#000000",
                  position: "relative",
                  minHeight: 0,
                  flex: 1,
                } as React.CSSProperties
              }
            >
              {/* Video container with relative positioning for button overlay */}
              <div
                className={
                  isReelMode && !isMobile
                    ? "w-full h-full min-h-0 flex items-stretch justify-center relative"
                    : "w-full h-full flex items-center justify-center relative"
                }
                style={{ position: "relative", width: "100%", height: "100%" }}
              >
                {/* Thumbnail-as-loading (blur-up) until real playback — never empty black + native bar */}
                {effectivePlaybackId && (
                  <MuxPosterBackdrop
                    playbackId={effectivePlaybackId}
                    fitMode={thumbnailFitMode}
                    hidden={hasPlaybackStarted}
                    className="z-[24] pointer-events-none"
                  />
                )}
                {awaitingFirstPlayback && (
                  <div
                    className="absolute inset-0 z-[23] overflow-hidden pointer-events-none"
                    aria-hidden
                  >
                    <div className={`mux-load-shimmer-${safeCssId}`} />
                  </div>
                )}
                <MuxPlayer
                  key={`muxp-${safeCssId}-r${retryCount}`}
                  ref={playerRef}
                  streamType="on-demand"
                  playbackId={effectivePlaybackId}
                  accentColor="#ac39f2"
                  // TikTok Shop style: NO controls in any country - immersive experience (via CSS)
                  // Intelligent autoplay: try with sound first, fallback to muted (TikTok/Reels style)
                  // Instagram Explorer often blocks autoplay with sound, so use "any" for fallback.
                  // Extra post-play unmute attempts added to start with sound whenever possible (for membership video landings).
                  // Integrated with admin real previews (via viewportScope=container + hideUnmute=false) and simulation (worker injects based on ?simulate_vpn / ?simulate_ip).
                  // Can be disabled with autoPlay={false} for manual play only
                  autoPlay={autoPlay}
                  loop={loop}
                  muted={isInstagram || isMuted}
                  playsInline
                  // Optimize preload strategy for mobile networks:
                  // - "metadata": Fast initial load, user can start watching quickly
                  // - "auto": Desktop with good connection can buffer ahead
                  preload={
                    isMobile || isInstagram || muxArcPlaybackTune
                      ? "metadata"
                      : "auto"
                  }
                  // Disable keyboard shortcuts (not needed for mobile/touch)
                  nohotkeys
                  // Progressive thumbnail loading for instant visual feedback
                  poster={thumbnailUrl || undefined}
                  placeholder={placeholderUrl || undefined}
                  // Always force inline for mobile + Instagram/FB/Threads WebView (prevents jumping to native player or breaking our responsive frame)
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  x5-video-player-type="h5"
                  x5-video-player-fullscreen="false"
                  // Extra for older WebViews / IG
                  {...(isIOS || isInstagram
                    ? { "webkit-playsinline": "true", "x5-playsinline": "true" }
                    : {})}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onEnded={handleEnded}
                  onError={handleMuxPlayerError}
                  onLoadedMetadata={handleLoadedMetadata}
                  onLoadedData={handleMuxLoadedData}
                  onCanPlay={handleCanPlay}
                  onTimeUpdate={handleTimeUpdateForTelemetry}
                  onStalled={() => {
                    logVideoEvent({
                      event: "video_stalled",
                      country: effectiveCountry,
                      service,
                      playbackId: effectivePlaybackId,
                    });
                  }}
                  onWaiting={() => {
                    logVideoEvent({
                      event: "video_buffering",
                      country: effectiveCountry,
                      service,
                      playbackId: effectivePlaybackId,
                    });
                  }}
                  style={
                    {
                      width: "100%",
                      height: "100%",
                      // Use official Mux CSS variables for video object fit
                      "--media-object-fit":
                        isReelMode && isMobile ? "cover" : "contain",
                      "--media-object-position": "center",
                      ...MUX_HIDE_NATIVE_CONTROLS_VARS,
                      // Mobile GPU acceleration for smoother playback
                      ...(isMobile
                        ? {
                            transform: "translateZ(0)",
                            backfaceVisibility: "hidden",
                            WebkitBackfaceVisibility: "hidden",
                            WebkitFontSmoothing: "antialiased",
                          }
                        : {}),
                    } as React.CSSProperties & Record<`--${string}`, string>
                  }
                  envKey={muxDataEnvKey}
                  metadata={muxDataMetadata}
                  // Performance optimizations for Mux
                  startTime={0}
                  primaryColor="#ac39f2"
                  secondaryColor="#ec4899"
                  // capRenditionToPlayerSize already limits rendition size on mobile without
                  // restricting the manifest to specific codec profiles. maxResolution="720p"
                  // caused manifestIncompatibleCodecsError on Android/some browsers because
                  // it filtered the manifest to only H.264 High Profile variants which fail
                  // MSE codec checks on those platforms.
                  // https://docs.mux.com/guides/player-advanced-usage#change-playback-engine
                  // Arc: forzar MSE; Instagram WebView: native comprobado.
                  {...(isInstagram
                    ? { preferPlayback: "native" as const }
                    : muxArcPlaybackTune
                      ? { preferPlayback: "mse" as const }
                      : {})}
                  // Thumbnail optimization: Show frame at 0s for instant preview
                  thumbnailTime={0}
                  // @ts-expect-error - minPreloadSegments removed from Mux types but still works at runtime
                  minPreloadSegments={2}
                  // Cap rendition to actual player dimensions for all devices — avoids serving
                  // 1080p+ to a ~600px-wide 9:16 card on desktop, which causes segment-boundary
                  // stalls (observed every ~9s for US desktop visitors on 4G).
                  capRenditionToPlayerSize={true}
                  // Audio optimizations
                  defaultShowRemainingTime={false}
                  {...(fragileMseHlsConfig
                    ? { _hlsConfig: fragileMseHlsConfig }
                    : {})}
                />
              </div>

              {/* Descuento suerte: muestra después de tiempo de reproducción (no molesta el inicio) */}
              {showWatcherLuckBanner &&
                stripeWatcherLuckEligible &&
                stripeWatcherLuckPercent > 0 &&
                !showHeavyVideoLoader && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                    style={{
                      bottom:
                        "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                    }}
                  >
                    <div
                      className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-1.5 rounded-lg border border-white/10 bg-black/38 px-2.5 py-1.5 shadow-md backdrop-blur-sm"
                      role="status"
                      aria-live="polite"
                    >
                      <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                        <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                        {" "}
                        {detectedLanguage === "es"
                          ? "extra en membresía (Stripe)."
                          : "extra on membership (Stripe)."}
                      </p>
                      <button
                        type="button"
                        aria-label={
                          detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                        }
                        className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 motion-reduce:transition-none"
                        onClick={dismissWatcherLuckBanner}
                      >
                        <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                      </button>
                    </div>
                  </div>
                )}

              {/* Instagram browser suggestion banner (dismissible, non-intrusive) */}
              {showInstagramBanner && isInstagram && !showHeavyVideoLoader && (
                <div
                  className="absolute top-0 left-0 right-0 z-50 mx-4 animate-slide-down"
                  style={{
                    animation: "slideDown 0.3s ease-out",
                    marginTop: "max(1rem, env(safe-area-inset-top, 12px))",
                  }}
                >
                  <div className="bg-black/85 backdrop-blur-md rounded-2xl shadow-2xl border border-white/15 overflow-hidden">
                    <div className="p-4 flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <ExternalLink
                          className="w-5 h-5 text-white"
                          strokeWidth={2.5}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium leading-tight mb-1">
                          {linkCopiedForBrowser
                            ? t("linkCopied", detectedLanguage)
                            : t("betterExperience", detectedLanguage)}
                        </p>
                        {linkCopiedForBrowser ? (
                          <div className="mt-1 space-y-0.5">
                            <p className="text-white/90 text-xs">
                              {t("pasteInBrowser", detectedLanguage)}
                            </p>
                            {isIOS && (
                              <a
                                href={
                                  typeof window !== "undefined"
                                    ? window.location.href
                                    : "#"
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/80 text-xs underline block truncate max-w-full"
                              >
                                {t("longPressHint", detectedLanguage)}
                              </a>
                            )}
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={handleOpenInBrowser}
                              className="mt-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-semibold active:bg-white/90 md:hover:bg-white/95 transition-colors shadow-lg"
                            >
                              {t("openInBrowser", detectedLanguage)}
                            </button>
                            {isAndroid && (
                              <p className="text-white/75 text-xs mt-2 leading-snug">
                                {t("inAppBrowserMenuHint", detectedLanguage)}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleDismissInstagramBanner}
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                        aria-label="Dismiss"
                      >
                        <X className="w-5 h-5" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
              {showCTAButton && (
                <div
                  className={`action-button-container ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    zIndex: 100,
                    // Garantiza visibilidad encima del loading overlay (z-40) y thumbnail (z-30)
                    isolation: "isolate",
                  }}
                >
                  {muxFriendlyBanner && (
                    <p
                      className="text-white/45 text-[10px] sm:text-[11px] text-center mb-1.5 px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                      role="note"
                    >
                      {muxFriendlyBanner}
                    </p>
                  )}
                  {ctaSubtitle && (
                    <p className="cta-subtitle text-white/90 text-sm md:text-base text-center mb-2 drop-shadow-md">
                      {ctaSubtitle}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleCTAClick}
                    onTouchStart={handleCTATouchStart}
                    onTouchEnd={handleCTATouchEnd}
                    disabled={!showCTAButton || ctaBusy}
                    className="cta-action-button"
                    aria-label={ctaLabel}
                    aria-disabled={!showCTAButton || ctaBusy}
                    // touch-action: manipulation elimina el delay de 300ms en Android/iOS
                    // y desactiva gestos no deseados (zoom, scroll) en el botón
                    style={{ touchAction: "manipulation" }}
                  >
                    {ctaBusy ? (
                      <Loader2
                        className={`${
                          isMobile ? "w-4 h-4" : "w-5 h-5"
                        } shrink-0 animate-spin pointer-events-none`}
                        aria-hidden
                      />
                    ) : (
                      showInstagramIcon && (
                        <Instagram
                          className={`${
                            isMobile ? "w-4 h-4" : "w-5 h-5"
                          } shrink-0 pointer-events-none`}
                          strokeWidth={2.5}
                          style={{
                            filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                          }}
                        />
                      )
                    )}
                    <span className="cta-button-text">{ctaLabel}</span>
                    {!ctaBusy && (
                      <ExternalLink
                        className="cta-button-icon action-button-icon"
                        strokeWidth={2.5}
                      />
                    )}
                  </button>
                </div>
              )}

              {/* Progressive image loading: LQIP (Low Quality Image Placeholder) → Full thumbnail */}
              {/* Shows instantly while video buffers, crucial for mobile UX */}
              {awaitingFirstPlayback &&
                (placeholderUrl || thumbnailUrl) &&
                !thumbnailError && (
                  <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                    {/* LQIP - Blur-up effect for instant perceived load */}
                    {placeholderUrl && (
                      <div
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                          backgroundImage: `url(${placeholderUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          filter: "blur(20px)",
                          transform: "scale(1.1)", // Prevent blur edges
                          opacity: thumbnailUrl ? 0.5 : 1,
                        }}
                      />
                    )}
                    {/* Full quality thumbnail - fades in when loaded */}
                    {thumbnailUrl && (
                      <div
                        className="absolute inset-0 animate-fade-in"
                        style={{
                          backgroundImage: `url(${thumbnailUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          animationDuration: "0.5s",
                          animationFillMode: "both",
                        }}
                      />
                    )}
                    {/* Dark overlay for better spinner visibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
                  </div>
                )}

              {renderMuxLoadChrome()}

              {/* Audio indicator — sutil. Position configurable via prop:
                  - "top-right" (default): chip pequeño esquinado (uso general).
                  - "center": pill centrado en el medio del video — más visible
                    para guiar al user a activar sonido sin perder sutileza
                    (opacity 0.85, backdrop blur, ring sutil). Usado en surfaces
                    fullscreen tipo TikTok shop donde el indicator esquinado
                    pasa desapercibido.
                  Tap anywhere on video también unmutea (handler en el container). */}
              {showMuteIndicator && isPlaying && !hideUnmutePrompt && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMute();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMute();
                  }}
                  className={
                    unmuteIndicatorPosition === "center"
                      ? "absolute left-1/2 top-1/2 z-30 cursor-pointer touch-manipulation transition-opacity duration-300 md:hover:opacity-100"
                      : "absolute right-3 z-30 cursor-pointer touch-manipulation transition-opacity duration-300 md:hover:opacity-100"
                  }
                  style={
                    unmuteIndicatorPosition === "center"
                      ? {
                          transform: "translate(-50%, -50%)",
                          opacity: 0.85,
                          WebkitTapHighlightColor: "transparent",
                        }
                      : {
                          top: "max(0.75rem, env(safe-area-inset-top, 12px))",
                          opacity: 0.75,
                          WebkitTapHighlightColor: "transparent",
                        }
                  }
                  aria-label={t("enableAudio", detectedLanguage)}
                >
                  <div className="flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1.5 backdrop-blur-md ring-1 ring-white/15">
                    <svg
                      className="w-3.5 h-3.5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                      />
                      <line x1="18" y1="6" x2="6" y2="18" strokeWidth={2} />
                    </svg>
                    <span className="text-[11px] font-medium text-white/90 tracking-tight">
                      {t("tapForSound", detectedLanguage)}
                    </span>
                  </div>
                </button>
              )}

              {/* Play button overlay - shown if autoplay fails (sin texto de audio si hideUnmutePrompt) */}
              {!isPlaying && showPlayButton && (
                <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white/15 backdrop-blur-md border-4 border-white/40 flex items-center justify-center shadow-2xl transition-transform duration-200 active:scale-95 active:bg-white/25 md:hover:bg-white/20 md:hover:scale-[1.04] touch-manipulation select-none"
                    style={{
                      WebkitTapHighlightColor: "transparent",
                      WebkitTouchCallout: "none",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                      cursor: "pointer",
                      minWidth: "96px",
                      minHeight: "96px",
                    }}
                    aria-label={t("tapToPlayWithSound", detectedLanguage)}
                  >
                    <Play
                      className="w-12 h-12 md:w-14 md:h-14 text-white ml-1.5"
                      fill="currentColor"
                      strokeWidth={2}
                    />
                  </button>
                  {!hideUnmutePrompt && (
                    <div className="bg-black/75 text-white px-5 py-3 rounded-full text-base font-bold backdrop-blur-md flex items-center gap-2.5 shadow-2xl border border-white/25 ring-1 ring-white/10">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                        />
                      </svg>
                      <span className="tracking-tight">
                        {detectedLanguage === "en"
                          ? "🔊 Tap to play with sound"
                          : "🔊 Toca para reproducir con audio"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {onClose && (
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className={`mux-cta-close-btn fixed z-[200] min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 active:bg-black/90 text-white flex items-center justify-center transition-colors duration-200 touch-manipulation outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  style={{
                    top: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-top, 0.75rem))`,
                    right: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-right, 0.75rem))`,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  }}
                  aria-label="Cerrar"
                >
                  <X className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
          data-mux-reel-viewport={isReelMode && !isMobile ? "page" : undefined}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={handleContainerClick}
          role="presentation"
          aria-busy={awaitingFirstPlayback}
          style={
            {
              backgroundColor: "#000000",
              position: isReelMode && isMobile ? "fixed" : "relative",
              ...(isMobile
                ? {
                    // TikTok style: full screen immersive
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "100dvh",
                    maxHeight: "100dvh",
                    minHeight: "100vh",
                    overflow: "hidden",
                    width: "100vw",
                  }
                : {
                    minHeight: "100dvh",
                    ...(isReelMode
                      ? {
                          height: "100dvh",
                          minHeight: "100dvh",
                        }
                      : {}),
                  }),
              ...(isInstagram
                ? {
                    touchAction: "pan-y pinch-zoom",
                    WebkitTransform: "translateZ(0)",
                    transform: "translateZ(0)",
                  }
                : {}),
            } as React.CSSProperties
          }
        >
          {/* Video container: on mobile reel use absolute inset-0 so video fills entire screen */}
          <div
            className={
              isReelMode && !isMobile
                ? "w-full h-full min-h-[100dvh] flex items-stretch justify-center relative"
                : isReelMode && isMobile
                  ? "absolute inset-0"
                  : "w-full h-full flex items-center justify-center relative"
            }
            style={
              isReelMode && isMobile
                ? {
                    position: "absolute" as const,
                    inset: 0,
                    width: "100%",
                    height: "100%",
                  }
                : isReelMode && !isMobile
                  ? {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                      minHeight: "100dvh",
                    }
                  : {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                    }
            }
          >
            {effectivePlaybackId && (
              <MuxPosterBackdrop
                playbackId={effectivePlaybackId}
                fitMode={thumbnailFitMode}
                hidden={hasPlaybackStarted}
                className="z-[24] pointer-events-none"
              />
            )}
            {awaitingFirstPlayback && (
              <div
                className="absolute inset-0 z-[23] overflow-hidden pointer-events-none"
                aria-hidden
              >
                <div className={`mux-load-shimmer-${safeCssId}`} />
              </div>
            )}
            <MuxPlayer
              key={`muxp-${safeCssId}-r${retryCount}`}
              ref={playerRef}
              streamType="on-demand"
              playbackId={effectivePlaybackId}
              accentColor="#ac39f2"
              // TikTok Shop style: NO controls in any country - immersive experience (via CSS)
              // Intelligent autoplay: try with sound first, fallback to muted (TikTok/Reels style)
              // Instagram Explorer often blocks autoplay with sound, so use "any" for fallback
              // Can be disabled with autoPlay={false} for manual play only
              autoPlay={autoPlay}
              loop={loop}
              muted={isInstagram || isMuted}
              playsInline
              // Optimize preload strategy for mobile networks
              preload={
                isMobile || isInstagram || muxArcPlaybackTune
                  ? "metadata"
                  : "auto"
              }
              nohotkeys
              // Progressive thumbnail loading
              poster={thumbnailUrl || undefined}
              placeholder={placeholderUrl || undefined}
              // iOS-specific: Enable inline playback
              {...(isIOS
                ? { "webkit-playsinline": "true", "x5-playsinline": "true" }
                : {})}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              onError={handleMuxPlayerError}
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={handleMuxLoadedData}
              onCanPlay={handleCanPlay}
              onTimeUpdate={handleTimeUpdateForTelemetry}
              onStalled={() => {
                logVideoEvent({
                  event: "video_stalled",
                  country: effectiveCountry,
                  service,
                  playbackId: effectivePlaybackId,
                });
              }}
              onWaiting={() => {
                logVideoEvent({
                  event: "video_buffering",
                  country: effectiveCountry,
                  service,
                  playbackId: effectivePlaybackId,
                });
              }}
              style={
                {
                  width: "100%",
                  height: "100%",
                  // Use official Mux CSS variables for video object fit
                  "--media-object-fit":
                    isReelMode && isMobile ? "cover" : "contain",
                  "--media-object-position": "center",
                  ...MUX_HIDE_NATIVE_CONTROLS_VARS,
                  // Mobile GPU acceleration
                  ...(isMobile
                    ? {
                        transform: "translateZ(0)",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        WebkitFontSmoothing: "antialiased",
                      }
                    : {}),
                } as React.CSSProperties & Record<`--${string}`, string>
              }
              envKey={muxDataEnvKey}
              metadata={muxDataMetadata}
              // Performance optimizations for Mux
              startTime={0}
              primaryColor="#ac39f2"
              secondaryColor="#ec4899"
              // capRenditionToPlayerSize already limits rendition size on mobile without
              // restricting the manifest to specific codec profiles. maxResolution="720p"
              // caused manifestIncompatibleCodecsError on Android/some browsers because
              // it filtered the manifest to only H.264 High Profile variants which fail
              // MSE codec checks on those platforms.
              // https://docs.mux.com/guides/player-advanced-usage#change-playback-engine
              // Arc: forzar MSE; Instagram WebView: native comprobado.
              {...(isInstagram
                ? { preferPlayback: "native" as const }
                : muxArcPlaybackTune
                  ? { preferPlayback: "mse" as const }
                  : {})}
              // Thumbnail optimization for faster perceived load
              thumbnailTime={0}
              // @ts-expect-error - minPreloadSegments removed from Mux types but still works at runtime
              minPreloadSegments={2}
              capRenditionToPlayerSize={true}
              // Audio optimizations
              defaultShowRemainingTime={false}
              {...(fragileMseHlsConfig
                ? { _hlsConfig: fragileMseHlsConfig }
                : {})}
            />
          </div>

          {showWatcherLuckBanner &&
            stripeWatcherLuckEligible &&
            stripeWatcherLuckPercent > 0 &&
            !showHeavyVideoLoader && (
              <div
                className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                style={{
                  bottom:
                    "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                }}
              >
                <div
                  className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-1.5 rounded-lg border border-white/10 bg-black/38 px-2.5 py-1.5 shadow-md backdrop-blur-sm"
                  role="status"
                  aria-live="polite"
                >
                  <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                    <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                    {" "}
                    {detectedLanguage === "es"
                      ? "extra en membresía (Stripe)."
                      : "extra on membership (Stripe)."}
                  </p>
                  <button
                    type="button"
                    aria-label={
                      detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                    }
                    className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 motion-reduce:transition-none"
                    onClick={dismissWatcherLuckBanner}
                  >
                    <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                  </button>
                </div>
              </div>
            )}

          {/* Instagram browser suggestion banner (dismissible, non-intrusive) */}
          {showInstagramBanner && isInstagram && !showHeavyVideoLoader && (
            <div
              className="absolute top-0 left-0 right-0 z-50 mx-4 animate-slide-down"
              style={{
                animation: "slideDown 0.3s ease-out",
                marginTop: "max(1rem, env(safe-area-inset-top, 12px))",
              }}
            >
              <div className="bg-black/85 backdrop-blur-md rounded-2xl shadow-2xl border border-white/15 overflow-hidden">
                <div className="p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <ExternalLink
                      className="w-5 h-5 text-white"
                      strokeWidth={2.5}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium leading-tight mb-1">
                      {linkCopiedForBrowser
                        ? t("linkCopied", detectedLanguage)
                        : t("betterExperience", detectedLanguage)}
                    </p>
                    {linkCopiedForBrowser ? (
                      <div className="mt-1 space-y-0.5">
                        <p className="text-white/90 text-xs">
                          {t("pasteInBrowser", detectedLanguage)}
                        </p>
                        {isIOS && (
                          <a
                            href={
                              typeof window !== "undefined"
                                ? window.location.href
                                : "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/80 text-xs underline block truncate max-w-full"
                          >
                            {t("longPressHint", detectedLanguage)}
                          </a>
                        )}
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleOpenInBrowser}
                          className="mt-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-semibold active:bg-white/90 md:hover:bg-white/95 transition-colors shadow-lg"
                        >
                          {t("openInBrowser", detectedLanguage)}
                        </button>
                        {isAndroid && (
                          <p className="text-white/75 text-xs mt-2 leading-snug">
                            {t("inAppBrowserMenuHint", detectedLanguage)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleDismissInstagramBanner}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-5 h-5" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
          {showCTAButton && (
            <div
              className={`action-button-container ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                zIndex: 100,
                isolation: "isolate",
              }}
            >
              {muxFriendlyBanner && (
                <p
                  className="text-white/45 text-[10px] sm:text-[11px] text-center mb-1.5 px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                  role="note"
                >
                  {muxFriendlyBanner}
                </p>
              )}
              {ctaSubtitle && (
                <p className="cta-subtitle text-white/90 text-sm md:text-base text-center mb-2 drop-shadow-md">
                  {ctaSubtitle}
                </p>
              )}
              <button
                type="button"
                onClick={handleCTAClick}
                onTouchStart={handleCTATouchStart}
                onTouchEnd={handleCTATouchEnd}
                disabled={!showCTAButton || ctaBusy}
                className="cta-action-button"
                aria-label={ctaLabel}
                aria-disabled={!showCTAButton || ctaBusy}
                style={{ touchAction: "manipulation" }}
              >
                {ctaBusy ? (
                  <Loader2
                    className={`${
                      isMobile ? "w-4 h-4" : "w-5 h-5"
                    } shrink-0 animate-spin pointer-events-none`}
                    aria-hidden
                  />
                ) : (
                  showInstagramIcon && (
                    <Instagram
                      className={`${
                        isMobile ? "w-4 h-4" : "w-5 h-5"
                      } shrink-0 pointer-events-none`}
                      strokeWidth={2.5}
                      style={{
                        filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  )
                )}
                <span className="cta-button-text">{ctaLabel}</span>
                {!ctaBusy && (
                  <ExternalLink
                    className="cta-button-icon action-button-icon"
                    strokeWidth={2.5}
                  />
                )}
              </button>
            </div>
          )}

          {/* Progressive image loading: LQIP → Full thumbnail (mobile-optimized) */}
          {awaitingFirstPlayback &&
            (placeholderUrl || thumbnailUrl) &&
            !thumbnailError && (
              <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                {/* LQIP - Blur-up effect */}
                {placeholderUrl && (
                  <div
                    className="absolute inset-0 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url(${placeholderUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      filter: "blur(20px)",
                      transform: "scale(1.1)",
                      opacity: thumbnailUrl ? 0.5 : 1,
                    }}
                  />
                )}
                {/* Full quality thumbnail */}
                {thumbnailUrl && (
                  <div
                    className="absolute inset-0 animate-fade-in"
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      animationDuration: "0.5s",
                      animationFillMode: "both",
                    }}
                  />
                )}
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
              </div>
            )}

          {renderMuxLoadChrome()}

          {/* Audio indicator — TikTok-Shop style sutil: chip pequeño top-right
              con icon muted + label corto. No pulse, no gradient, no big shadow. */}
          {showMuteIndicator && isPlaying && !hideUnmutePrompt && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleMute();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleMute();
              }}
              className={
                unmuteIndicatorPosition === "center"
                  ? "absolute left-1/2 top-1/2 z-30 cursor-pointer touch-manipulation transition-opacity duration-300 md:hover:opacity-100"
                  : "absolute right-3 z-30 cursor-pointer touch-manipulation transition-opacity duration-300 md:hover:opacity-100"
              }
              style={
                unmuteIndicatorPosition === "center"
                  ? {
                      transform: "translate(-50%, -50%)",
                      opacity: 0.85,
                      WebkitTapHighlightColor: "transparent",
                    }
                  : {
                      top: "max(0.75rem, env(safe-area-inset-top, 12px))",
                      opacity: 0.75,
                      WebkitTapHighlightColor: "transparent",
                    }
              }
              aria-label={t("enableAudio", detectedLanguage)}
            >
              <div className="flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1.5 backdrop-blur-md ring-1 ring-white/15">
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                  <line x1="18" y1="6" x2="6" y2="18" strokeWidth={2} />
                </svg>
                <span className="text-[11px] font-medium text-white/90 tracking-tight">
                  {t("tapForSound", detectedLanguage)}
                </span>
              </div>
            </button>
          )}

          {!isPlaying && showPlayButton && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white/15 backdrop-blur-md border-4 border-white/40 flex items-center justify-center shadow-2xl transition-transform duration-200 active:scale-95 active:bg-white/25 md:hover:bg-white/20 md:hover:scale-[1.04] touch-manipulation select-none"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  WebkitTouchCallout: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  cursor: "pointer",
                  minWidth: "96px",
                  minHeight: "96px",
                }}
                aria-label={t("tapToPlayWithSound", detectedLanguage)}
              >
                <Play
                  className="w-12 h-12 md:w-14 md:h-14 text-white ml-1.5"
                  fill="currentColor"
                  strokeWidth={2}
                />
              </button>
              {!hideUnmutePrompt && (
                <div className="bg-black/75 text-white px-5 py-3 rounded-full text-base font-bold backdrop-blur-md flex items-center gap-2.5 shadow-2xl border border-white/25 ring-1 ring-white/10">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                  <span className="tracking-tight">
                    {detectedLanguage === "en"
                      ? "🔊 Tap to play with sound"
                      : "🔊 Toca para reproducir con audio"}
                  </span>
                </div>
              )}
            </div>
          )}

          {onClose && (
            <button
              ref={closeButtonRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`mux-cta-close-btn absolute z-50 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors touch-manipulation outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              style={{
                top: "max(1rem, env(safe-area-inset-top))",
                right: "max(1rem, env(safe-area-inset-right))",
              }}
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 shrink-0" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}
    </>
  );
};

/** Wrapper: kill-switch before hooks so disabled pages never mount MuxPlayer. */
export default function MuxVideoPlayerWithCTAGate(
  props: MuxVideoPlayerWithCTAProps,
) {
  if (!isMuxVideoDisplayEnabled()) return null;
  return <MuxVideoPlayerWithCTA {...props} />;
}
